import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { 
  Upload, 
  X, 
  Download, 
  Copy, 
  Loader2,
  CheckCircle,
  Video,
  Play,
  Pause,
  Layers,
  GripVertical,
  Scissors,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  Music,
  Search,
  Volume2,
  VolumeX,
  Clock,
  AlertTriangle,
  Zap,
  Film,
  Settings,
  Star,
  RefreshCw,
  ArrowUpDown,
  LayoutTemplate,
  Send,
  Sparkles,
  TrendingDown,
  BadgeCheck,
  Lock,
  Circle,
  CheckCircle2,
  Wand2,
  History,
  PlusCircle,
  MinusCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import "./SocialManagerPortal.css";
import CreatomateBatchResults from './CreatomateBatchResults';
import RenderHistory from './RenderHistory';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import VideoGenerationLoader from './VideoGenerationLoader';
import { DEFAULT_OVERLAYS } from './PropertyOverlays';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppHeader } from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIDEO_PRESETS = [
  { key: "portrait_9_16", label: "Portrait 9:16", dimensions: "1080×1920", primary: true },
  { key: "portrait_4_5", label: "Portrait 4:5", dimensions: "1080×1350" },
  { key: "square_1_1", label: "Square 1:1", dimensions: "1080×1080" },
  { key: "landscape_16_9", label: "Landscape 16:9", dimensions: "1920×1080" },
];

const CROP_MODES = [
  { value: "auto", label: "Auto (Subject Tracking)" },
  { value: "center", label: "Centre Crop" },
];

const EDIT_MODES = [
  { value: "default", label: "Sneak Peek", description: "1.4s quick cuts (default)", icon: Film },
  { value: "short", label: "Short Edit", description: "3–5s per clip · longer video", icon: Zap },
  { value: "ai_timing", label: "AI Timing", description: "Quick 2-beat cuts", icon: Music },
  { value: "ai_plus", label: "AI+", description: "Mixed 2 & 4 beat flow", icon: Music },
  { value: "smart_ai", label: "Smart AI", description: "Fast intro · slow build · fast finish", icon: Music },
  { value: "drop_ai", label: "Drop AI", description: "Featured clips hit the drops", icon: Zap },
];

// Batch outputs: Sneak Peek always renders (plain, Edit Mode timing); reels are toggled on/off
// and render from their Reels Styler master (or any Creatomate account template) with a shared 3–5s cut.
const OUTPUT_KINDS = [
  { value: "sneak_peek", label: "Sneak Peek", icon: Film, always: true },
  { value: "just_listed", label: "Just Listed", icon: Sparkles },
  { value: "price_reduction", label: "Price Reduction", icon: TrendingDown },
  { value: "recently_sold", label: "Recently Sold", icon: BadgeCheck },
];
const DEFAULT_OUTPUT_CONFIG = {
  sneak_peek: { enabled: true, template: "plain" },
  just_listed: { enabled: false, template: "master" },
  price_reduction: { enabled: false, template: "master" },
  recently_sold: { enabled: false, template: "master" },
};
const OUTPUT_CONFIG_KEY = "svb-output-config-v1";
// PARKED: the "Load Clips / Output Formats" panel. Clips arrive via My Listings / Video Search
// prefill now; the dimensions picker may come back later — flip this to show it again.
const SHOW_LOAD_PANEL = false;
const AUTO_SMART_THRESHOLD = 15;   // auto-run Smart Select when a job loads more clips than this
const ROOM_ORDER = ["exterior_front", "hallway", "kitchen", "living", "ground_other", "stairs", "bedroom", "bathroom", "garden", "other"];
// Only the per-tab TEMPLATE choice is remembered; reels always start switched OFF.
const loadOutputConfig = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(OUTPUT_CONFIG_KEY) || "null");
    if (!saved) return DEFAULT_OUTPUT_CONFIG;
    const merged = {};
    Object.keys(DEFAULT_OUTPUT_CONFIG).forEach((k) => {
      merged[k] = { ...DEFAULT_OUTPUT_CONFIG[k], template: saved[k]?.template || DEFAULT_OUTPUT_CONFIG[k].template };
    });
    return merged;
  } catch (_) { return DEFAULT_OUTPUT_CONFIG; }
};

// Helper to format seconds to MM:SS (or 0.Xs for sub-second clips)
const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "";
  if (seconds < 1) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Helper to parse MM:SS to seconds
const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length === 2) {
    const mins = parseInt(parts[0]) || 0;
    const secs = parseInt(parts[1]) || 0;
    return mins * 60 + secs;
  }
  return parseFloat(timeStr) || null;
};

const LOGO_POSITIONS = [
  { value: "north_west", label: "Top Left" },
  { value: "north", label: "Top Centre" },
  { value: "north_east", label: "Top Right" },
  { value: "west", label: "Middle Left" },
  { value: "center", label: "Centre" },
  { value: "east", label: "Middle Right" },
  { value: "south_west", label: "Bottom Left" },
  { value: "south", label: "Bottom Centre" },
  { value: "south_east", label: "Bottom Right" },
];

// Unified sortable clip card — single list for all clips
function SortableClipCard({ clip, idx, totalCount, isExpanded, onToggleTrim, onToggleStar, onRemove, onMoveUp, onMoveDown, onTrimChange, formatDuration, formatTime, showStar, onPreview, isPreviewing, onToggleInclude }) {
  const excluded = clip.includeInEdit === false;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: clip.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`clip-card-unified ${isDragging ? 'dragging' : ''} ${clip.isStarred ? 'starred' : ''} ${isExpanded ? 'trim-expanded' : ''} ${excluded ? 'is-excluded' : ''}`}
      data-testid={`clip-card-${clip.id}`}
      data-included={excluded ? 'false' : 'true'}
    >
      <div className="clip-card-row">
        <div 
          className="clip-grip"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </div>
        <span className="clip-order-num">{idx + 1}</span>
        <div
          className={`clip-thumb clip-thumb-hoverable ${isPreviewing ? 'previewing' : ''}`}
          onMouseEnter={() => onPreview && onPreview(clip)}
          onClick={() => onPreview && onPreview(clip)}
          title="Preview this clip"
          data-testid={`clip-preview-thumb-${clip.id}`}
        >
          {clip.thumbnail_url ? (
            <img src={clip.thumbnail_url} alt={clip.original_filename} className="clip-thumb-img" />
          ) : clip.r2Url || clip.url ? (
            <video 
              src={`${clip.r2Url || clip.url}#t=0.5`} 
              preload="metadata"
              muted
              playsInline
              className="clip-thumb-video"
            />
          ) : (
            <div className="clip-thumb-placeholder"><Video size={16} /></div>
          )}
          <span className="clip-thumb-play"><Play size={14} /></span>
          <span className="clip-duration">{formatDuration(clip.duration)}</span>
        </div>
        <div className="clip-info">
          <span className="clip-name">
            {clip.room?.label && <span className={`room-tag room-${clip.room.category}`} data-testid={`room-tag-${clip.id}`}>{clip.room.label}</span>}
            {clip.original_filename}
          </span>
          <div className="clip-meta">
            {excluded ? (
              <span className="skip-badge" data-testid={`skip-reason-${clip.id}`}>Skipped{clip.skipReason ? ` · ${clip.skipReason}` : ''}</span>
            ) : (clip.startTime !== null || clip.endTime !== null) && (
              <span className="trim-badge">
                <Scissors size={10} />
                {formatTime((clip.endTime || clip.duration) - (clip.startTime || 0))}
              </span>
            )}
          </div>
        </div>
        <div className="clip-actions">
          {onToggleInclude && (
            <button
              className={`include-toggle ${excluded ? 'is-off' : ''}`}
              onClick={() => onToggleInclude(clip.id)}
              title={excluded ? "Include this clip in the edit" : "Skip this clip"}
              data-testid={`include-toggle-${clip.id}`}
            >
              {excluded ? <PlusCircle size={14} /> : <MinusCircle size={14} />}
              {excluded && <span>Include</span>}
            </button>
          )}
          {showStar && (
            <button
              className={`star-toggle ${clip.isStarred ? 'active' : ''}`}
              onClick={() => onToggleStar(clip.id)}
              title={clip.isStarred ? "Remove from feature clips" : "Star as feature clip"}
              data-testid={`star-toggle-${clip.id}`}
            >
              <Star size={14} className={clip.isStarred ? 'star-filled' : ''} />
            </button>
          )}
          <button
            className={`trim-toggle ${isExpanded ? 'active' : ''}`}
            onClick={() => onToggleTrim(clip.id)}
            title="Trim clip"
            data-testid={`trim-toggle-${clip.id}`}
          >
            <Scissors size={14} />
          </button>
          <div className="clip-arrows">
            <button 
              className="clip-arrow"
              onClick={() => onMoveUp(clip.id)}
              disabled={idx === 0}
              title="Move up"
            >
              <ChevronUp size={14} />
            </button>
            <button 
              className="clip-arrow"
              onClick={() => onMoveDown(clip.id)}
              disabled={idx === totalCount - 1}
              title="Move down"
            >
              <ChevronDown size={14} />
            </button>
          </div>
          <button
            className="remove-clip"
            onClick={() => onRemove(clip)}
            title="Remove"
            data-testid={`remove-clip-${clip.id}`}
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <VideoTrimPreview clip={clip} onTrimChange={onTrimChange} />
      )}
    </div>
  );
}

// Video Trim Preview Component with visual slider
function VideoTrimPreview({ clip, onTrimChange }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [localStart, setLocalStart] = useState(clip.startTime || 0);
  const [localEnd, setLocalEnd] = useState(clip.endTime || clip.duration);

  useEffect(() => {
    setLocalStart(clip.startTime || 0);
    setLocalEnd(clip.endTime || clip.duration);
  }, [clip.startTime, clip.endTime, clip.duration]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.currentTime = localStart;
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      // Stop at end point
      if (time >= localEnd) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleSliderChange = (values) => {
    const [start, end] = values;
    setLocalStart(start);
    setLocalEnd(end);
    if (videoRef.current) {
      videoRef.current.currentTime = start;
    }
  };

  const handleSliderCommit = () => {
    onTrimChange(clip.id, localStart, localEnd);
  };

  const trimDuration = localEnd - localStart;

  return (
    <div className="video-trim-preview" data-testid={`trim-preview-${clip.id}`}>
      <div className="trim-video-container">
        <video
          ref={videoRef}
          src={clip.secure_url}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          preload="metadata"
        />
        <button 
          className="trim-play-btn"
          onClick={handlePlayPause}
          data-testid={`trim-play-${clip.id}`}
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
        <div className="trim-time-overlay">
          {formatTime(currentTime)} / {formatTime(clip.duration)}
        </div>
      </div>
      
      <div className="trim-slider-section">
        <div className="trim-slider-labels">
          <span>Start: {formatTime(localStart)}</span>
          <span className="trim-duration-badge">
            <Scissors size={12} />
            {formatTime(trimDuration)} selected
          </span>
          <span>End: {formatTime(localEnd)}</span>
        </div>
        <Slider
          value={[localStart, localEnd]}
          min={0}
          max={clip.duration}
          step={0.1}
          onValueChange={handleSliderChange}
          onValueCommit={handleSliderCommit}
          className="trim-range-slider"
          data-testid={`trim-slider-${clip.id}`}
        />
        <div className="trim-markers">
          <span>0:00</span>
          <span>{formatTime(clip.duration)}</span>
        </div>
      </div>
    </div>
  );
}

// Music Track Component
function MusicTrackCard({ track, isSelected, onSelect, onPreview, isPlaying, loadError }) {
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={`music-track-card ${isSelected ? 'selected' : ''} ${loadError ? 'track-error' : ''}`}
      data-testid={`music-track-${track.id}`}
    >
      <div className="track-info">
        <div className="track-title">{track.title || track.name}</div>
        <div className="track-meta">
          {track.artist && <span className="track-user">by {track.artist}</span>}
          {track.duration > 0 && <span className="track-duration">{formatDuration(track.duration)}</span>}
          {track.vibe && <span className="track-vibe-tag">{track.vibe}</span>}
        </div>
      </div>
      <div className="track-actions">
        <button 
          className={`track-preview-btn ${isPlaying ? 'playing' : ''}`}
          onClick={() => onPreview(track)}
          title={isPlaying ? "Stop" : "Preview"}
          data-testid={`preview-track-${track.id}`}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button 
          className={`track-select-btn ${isSelected ? 'selected' : ''}`}
          onClick={() => onSelect(track)}
          title={isSelected ? "Selected" : "Use this track"}
          data-testid={`select-track-${track.id}`}
        >
          {isSelected ? <CheckCircle size={16} /> : <Music size={16} />}
        </button>
      </div>
      {loadError && <div className="track-error-msg">Failed to load audio</div>}
    </div>
  );
}

function SocialVideoBuilder({ onBack }) {
  const { user, impersonating } = useAuth();
  const canLoadCaptures = user?.role === 'super_admin' || impersonating;

  // Upload state
  const [uploadedClips, setUploadedClips] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expandedTrimId, setExpandedTrimId] = useState(null);
  const fileInputRef = useRef(null);

  // Recent NicheCam captures
  const [recentCaptures, setRecentCaptures] = useState([]);
  const [loadingCaptures, setLoadingCaptures] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState("default");

  // Music state
  const [showMusicPanel, setShowMusicPanel] = useState(false);
  const [musicSearchQuery, setMusicSearchQuery] = useState("");
  const [musicSearchResults, setMusicSearchResults] = useState([]);
  const [isSearchingMusic, setIsSearchingMusic] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [previewingTrackId, setPreviewingTrackId] = useState(null);
  const [trackLoadError, setTrackLoadError] = useState(null);
  const [musicVibes, setMusicVibes] = useState([]);
  const audioRef = useRef(null);

  // Beat-mapped tracks state (AI Timing)
  const [beatMappedTracks, setBeatMappedTracks] = useState([]);
  const [isLoadingBeatMapped, setIsLoadingBeatMapped] = useState(false);
  
  // Keep a ref to selectedMusic for stable access in async handlers
  const selectedMusicRef = useRef(null);
  useEffect(() => {
    selectedMusicRef.current = selectedMusic;
  }, [selectedMusic]);

  // DnD Kit sensors for touch-friendly drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Format options
  const [selectedFormats, setSelectedFormats] = useState(["portrait_9_16"]);
  const [cropMode, setCropMode] = useState("auto");

  // Branding
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState("north");
  const [logoScale, setLogoScale] = useState(60);
  const [logoPadding, setLogoPadding] = useState(10);
  const [logoOpacity, setLogoOpacity] = useState(100);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [showLogoSettings, setShowLogoSettings] = useState(false);

  // Per-client logo memory
  const [wmsClient, setWmsClient] = useState(null);
  const presetLoadedRef = useRef(false);
  const presetSaveTimer = useRef(null);

  useEffect(() => {
    if (!wmsClient?.id) return;
    presetLoadedRef.current = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/company/client-logo/${wmsClient.id}`);
        const preset = r.data.preset;
        if (preset?.logo) setCompanyLogo(preset.logo);
        const s = preset?.settings;
        if (s) {
          if (s.position) setLogoPosition(s.position);
          if (typeof s.scale === "number") setLogoScale(s.scale);
          if (typeof s.padding === "number") setLogoPadding(s.padding);
          if (typeof s.opacity === "number") setLogoOpacity(s.opacity);
          if (typeof s.enabled === "boolean") setLogoEnabled(s.enabled);
        }
        if (preset?.logo || s) {
          toast.success(`Logo preset loaded for ${wmsClient.name || "client"}`);
        }
      } catch (e) { /* no preset yet */ }
      setTimeout(() => { presetLoadedRef.current = true; }, 500);
    })();
  }, [wmsClient?.id]); // load preset when client changes

  useEffect(() => {
    if (!wmsClient?.id || !presetLoadedRef.current) return;
    clearTimeout(presetSaveTimer.current);
    presetSaveTimer.current = setTimeout(() => {
      axios.put(`${API}/company/client-logo/${wmsClient.id}`, {
        client_name: wmsClient.name || "",
        settings: { enabled: logoEnabled, position: logoPosition, scale: logoScale, padding: logoPadding, opacity: logoOpacity },
      }).catch(() => {});
    }, 800);
  }, [logoEnabled, logoPosition, logoScale, logoPadding, logoOpacity, wmsClient?.id]); // autosave placement

  // Stitching (legacy Cloudinary flag — Creatomate always stitches)
  const [stitchEnabled] = useState(true);
  // Active output tab (Sneak Peek / Just Listed / Price Reduction / Recently Sold)
  const [reelType, setReelType] = useState("sneak_peek");
  const [reelTexts] = useState({ line1: "", line2: "" }); // legacy Cloudinary reel overlay text (parked)
  const [stitchTransition] = useState("none");

  // Creatomate primary renderer — per-tab on/off + template, shared property details, batch results
  const [outputConfig, setOutputConfig] = useState(loadOutputConfig);
  const [propertyDetails, setPropertyDetails] = useState({ name: "", location: "", old_price: "", new_price: "" });
  const [batch, setBatch] = useState(null);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [creatomateTemplates, setCreatomateTemplates] = useState([]);
  const [reelMasters, setReelMasters] = useState([]);
  // Smart Select (vision-picked clips), render history + versioning
  const [smartSelect, setSmartSelect] = useState({ running: false, target: 12, applied: false, summary: null });
  const [parentBatch, setParentBatch] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/video/creatomate/templates`)
      .then((r) => setCreatomateTemplates(r.data.templates || []))
      .catch(() => {});
    axios.get(`${API}/video/creatomate/masters`)
      .then((r) => setReelMasters(r.data.masters || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const templatesOnly = Object.fromEntries(Object.entries(outputConfig).map(([k, v]) => [k, { template: v.template }]));
      localStorage.setItem(OUTPUT_CONFIG_KEY, JSON.stringify(templatesOnly));
    } catch (_) {}
  }, [outputConfig]);

  const setOutput = (kind, patch) =>
    setOutputConfig((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));

  // Overlays (text only now)
  // Legacy Cloudinary text overlays (UI removed; state kept for the parked renderer + preview)
  const [propertyOverlays] = useState(DEFAULT_OVERLAYS);
  const [sharedPosition] = useState('south_west');
  const [groupBackground] = useState({ enabled: true, color: 'rgba(0,0,0,0.6)', rounded: true });

  // Results
  const [generatedVideos, setGeneratedVideos] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [processingStep, setProcessingStep] = useState(0);
  const [submittedVideos, setSubmittedVideos] = useState({}); // { videoId: submission }
  const [submittingVideo, setSubmittingVideo] = useState(null); // videoId currently submitting
  // Social Library push state
  const [hubModal, setHubModal] = useState(null); // video being pushed
  const [hubBranches, setHubBranches] = useState(null); // lazy-loaded branch list
  const [hubForm, setHubForm] = useState({ branch_id: "", title: "", asset_type: "sneak_peek" });
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushed, setHubPushed] = useState({}); // { videoId: true }

  // WMS Job ID for loading clips from R2
  const [wmsJobId, setWmsJobId] = useState("");
  const [isLoadingFromWms, setIsLoadingFromWms] = useState(false);
  const [selectedBucket] = useState("nichecom");

  // Floorplan PDFs (Cat 2) for the loaded WMS job — lightbox for editors
  const [floorplans, setFloorplans] = useState([]);
  const [floorplanJobId, setFloorplanJobId] = useState(null);
  const [floorplanOpen, setFloorplanOpen] = useState(false);
  const [activeFloorplanIdx, setActiveFloorplanIdx] = useState(0);

  // Clip currently playing in the Live Preview panel (hover/click a thumbnail)
  const [previewClip, setPreviewClip] = useState(null);

  const fetchFloorplans = async (jobId) => {
    try {
      const res = await axios.get(`${API}/listings/${jobId}/floorplans`);
      const fps = res.data?.floorplans || [];
      setFloorplans(fps);
      setFloorplanJobId(jobId);
      setActiveFloorplanIdx(0);
    } catch (_) {
      setFloorplans([]);
    }
  };

  // Load clips from WMS R2 bucket
  const loadFromWmsJob = async (explicitJobId, options = {}) => {
    const jobId = (explicitJobId !== undefined ? explicitJobId : wmsJobId).toString().trim();
    if (!jobId) {
      toast.error("Please enter a Job ID");
      return;
    }

    setIsLoadingFromWms(true);
    
    try {
      const body = {
        job_id: jobId,
        bucket: selectedBucket,
      };
      if (options.productFilter) body.product_filter = options.productFilter;
      if (options.sources) body.sources = options.sources;

      const response = await axios.post(`${API}/social-video/load-job-videos`, body, { timeout: 120000 });

      // Remember which client this order belongs to (per-client logo presets)
      if (response.data.client?.id) {
        setWmsClient(response.data.client);
      }

      // Drop clips the user pre-vetted out in the videos lightbox
      let loadedClips = response.data.clips || [];
      const excludeSet = new Set((options.excludeNames || []).map(n => String(n).toLowerCase()));
      let excludedCount = 0;
      if (excludeSet.size) {
        const before = loadedClips.length;
        loadedClips = loadedClips.filter(c => !excludeSet.has((c.original_filename || c.name || '').toLowerCase()));
        excludedCount = before - loadedClips.length;
      }

      // Apply the client's saved clip order (from the clip-sort smart link)
      let clientOrderApplied = false;
      if (options.orderedNames && options.orderedNames.length) {
        const pos = new Map(options.orderedNames.map((n, i) => [String(n).toLowerCase(), i]));
        const rank = (c, idx) => {
          const key = (c.original_filename || c.name || '').toLowerCase();
          return pos.has(key) ? pos.get(key) : 100000 + idx;
        };
        loadedClips = loadedClips
          .map((c, idx) => ({ c, r: rank(c, idx) }))
          .sort((a, b) => a.r - b.r)
          .map(x => x.c);
        clientOrderApplied = loadedClips.some(c => pos.has((c.original_filename || c.name || '').toLowerCase()));
      }

      if (response.data.success && loadedClips.length > 0) {
        // Process clips: trust the backend duration when present (probed via
        // hachoir from the moov atom), otherwise fall back to browser metadata,
        // then file-size estimate, then a conservative 6s default.
        const clipsWithDuration = await Promise.all(
          loadedClips.map(async (clip, idx) => {
            let duration = Number(clip.duration) || 0;

            // If backend didn't have a duration, try browser metadata
            if (!duration) {
              duration = await getVideoDurationFromUrl(clip.url);
            }

            // File-size estimate at ~3.5 MB/s (iPhone 1080p HEVC)
            if (!duration && clip.bytes && clip.bytes > 0) {
              duration = clip.bytes / 3500000;
            }

            // Last-resort fallback: 6s (median property clip) — keeps trim
            // windows well within typical clip bounds and avoids the
            // 25s overshoot bug that caused freeze/judder/double-time.
            if (!duration || duration < 0.5) duration = 6;

            // Pre-populate safe default trim windows from the start. This is a
            // belt-and-braces guard: even if the user clicks Generate before
            // any edit mode applies (or AI Timing fires without music), the
            // backend still gets a valid so_/eo_ pair for every clip.
            const safeStart = Math.min(2, Math.max(0, duration * 0.15));
            const safeEnd = Math.max(safeStart + 1.0, Math.min(duration, safeStart + 3.5));

            return {
              ...clip,
              id: clip.id || `r2-${idx}-${Date.now()}`,
              sourceType: clip.sourceType || 'r2',
              duration,
              isStarred: false,
              startTime: safeStart,
              endTime: safeEnd,
              order: uploadedClips.length + idx
            };
          })
        );

        setUploadedClips(prev => [...prev, ...clipsWithDuration]);
        fetchFloorplans(jobId);
        if (!options.skipSmartSelect && clipsWithDuration.length > AUTO_SMART_THRESHOLD) {
          setTimeout(() => runSmartSelect(12, clipsWithDuration), 400);
        }
        const labelSuffix = options.productLabel ? ` (${options.productLabel})` : '';
        const excludeSuffix = excludedCount > 0 ? ` — ${excludedCount} excluded` : '';
        toast.success(`Loaded ${clipsWithDuration.length} video(s) from Job ${jobId}${labelSuffix}${excludeSuffix}`);
        if (clientOrderApplied) {
          toast.info("Client's saved clip order applied");
        }
        
        // Apply timing: for AI beat modes, re-apply beat map; otherwise apply edit mode
        if (editMode === "drop_ai" && selectedMusic) {
          setTimeout(() => applyDropAIToClips(selectedMusic), 100);
        } else if ((editMode === "ai_timing" || editMode === "ai_plus" || editMode === "smart_ai") && selectedMusic) {
          setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
        } else if (editMode !== "custom") {
          setTimeout(() => applyEditModeToClips(editMode), 100);
        }
      } else {
        toast.error(excludedCount > 0
          ? `All clips for Job ${jobId} were excluded in the pre-vet — nothing to load`
          : `No videos found for Job ID: ${jobId}`);
      }
    } catch (error) {
      console.error("Error loading from WMS:", error);
      const errorMsg = error.response?.data?.detail || "Failed to load videos from WMS";
      toast.error(errorMsg);
    } finally {
      setIsLoadingFromWms(false);
    }
  };

  // Auto-load videos when arriving from a My Listings click. The listings
  // page stashes the order_id (+ optional product filter) in sessionStorage
  // right before navigating; we pull it out, populate the input, and fire
  // the load straight away.
  const autoLoadFiredRef = useRef(false);
  useEffect(() => {
    if (autoLoadFiredRef.current) return;
    try {
      const prefill = sessionStorage.getItem('prefillWmsJobId');
      if (prefill) {
        autoLoadFiredRef.current = true;
        const productFilter = sessionStorage.getItem('prefillWmsProductFilter') || null;
        const productLabel = sessionStorage.getItem('prefillWmsProductLabel') || null;
        let excludeNames = null;
        let orderedNames = null;
        try { excludeNames = JSON.parse(sessionStorage.getItem('prefillWmsExcludeNames') || 'null'); } catch (_) {}
        try { orderedNames = JSON.parse(sessionStorage.getItem('prefillWmsOrderedNames') || 'null'); } catch (_) {}
        sessionStorage.removeItem('prefillWmsJobId');
        sessionStorage.removeItem('prefillWmsProductFilter');
        sessionStorage.removeItem('prefillWmsProductLabel');
        sessionStorage.removeItem('prefillWmsExcludeNames');
        sessionStorage.removeItem('prefillWmsOrderedNames');
        setWmsJobId(prefill);
        // Fire on next tick so the input visibly updates first.
        // No cancellation guard: if the component unmounts mid-flight the
        // fetch becomes a no-op anyway, and we never want StrictMode's
        // double-mount cleanup to swallow this one-shot side effect.
        setTimeout(() => loadFromWmsJob(prefill, { productFilter, productLabel, excludeNames, orderedNames }), 60);
      }
    } catch (_) {}
  }, []);


  // Fetch recent NicheCam captures (super admin only — incl. impersonating a branch user)
  useEffect(() => {
    if (!canLoadCaptures || !SHOW_LOAD_PANEL) return;
    const branchId = user?.branch_id;
    const isAdmin = user?.role === 'super_admin' || user?.role === 'client_manager';
    if (!branchId && !isAdmin) return;
    
    setLoadingCaptures(true);
    const url = branchId
      ? `${API}/social-video/recent-captures/${branchId}`
      : `${API}/social-video/recent-captures-all`;
    axios.get(url)
      .then(res => setRecentCaptures(res.data?.properties || []))
      .catch(() => {})
      .finally(() => setLoadingCaptures(false));
  }, [user?.branch_id, user?.role]);

  // Load all clips from a captured property into the editor
  const loadCapturedProperty = async (property) => {
    setIsLoadingFromWms(true);
    try {
      // Auto-set the WMS Job ID so it's used on approve/submit
      setWmsJobId(property.job_id);
      fetchFloorplans(property.job_id);

      const clipsWithDuration = await Promise.all(
        property.videos.map(async (v, idx) => {
          // 1. Trust the server-probed duration first — the backend reads the
          //    real QuickTime/MP4 mvhd atom in /api/listings/{id}/videos.
          // 2. Only fall back to HTML5 probing if the server didn't provide it
          //    (some .mov codecs can't be probed by `<video>` in the browser).
          // 3. The 6s last-resort matches every other code path — anything
          //    higher (the old 25s) causes AI Timing trim windows to seek
          //    past the actual end of typical 4-8s property clips, which
          //    renders as a freeze-frame stutter ("1 2 change change" pacing).
          let duration = Number(v.duration) || 0;
          if (!duration || duration < 0.5) {
            duration = await getVideoDurationFromUrl(v.url);
          }
          if (!duration && v.size > 0) duration = Math.round(v.size / 350000);
          if (!duration || duration < 0.5) duration = 6;
          return {
            id: `nichecam-${idx}-${Date.now()}`,
            sourceType: 'r2',
            r2Key: v.key,
            r2Url: v.url,
            public_id: v.key,
            url: v.url,
            secure_url: v.url,
            thumbnail_url: '',
            width: 0, height: 0,
            duration,
            format: v.filename.rsplit ? v.filename.split('.').pop().toLowerCase() : 'mov',
            original_filename: v.filename,
            bytes: v.size,
            order: uploadedClips.length + idx,
            isStarred: false,
            startTime: null,
            endTime: null,
            roomKey: v.room_key,
          };
        })
      );
      setUploadedClips(prev => [...prev, ...clipsWithDuration]);
      toast.success(`Loaded ${clipsWithDuration.length} clip(s) from ${property.address}`);
      if (editMode === "drop_ai" && selectedMusic) {
        setTimeout(() => applyDropAIToClips(selectedMusic), 100);
      } else if ((editMode === "ai_timing" || editMode === "ai_plus" || editMode === "smart_ai") && selectedMusic) {
        setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
      } else if (editMode !== "custom") {
        setTimeout(() => applyEditModeToClips(editMode), 100);
      }
    } catch (err) {
      console.error("Error loading captured property:", err);
      toast.error("Failed to load clips");
    } finally {
      setIsLoadingFromWms(false);
    }
  };

  // Helper to get video duration from URL
  const getVideoDurationFromUrl = (url) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      
      video.onerror = () => {
        resolve(0);
      };
      
      // Timeout after 30 seconds (large MOV files may take time)
      setTimeout(() => resolve(0), 30000);
      
      video.src = url;
    });
  };

  // Generate thumbnail from video URL using HTML5 canvas (no Cloudinary)
  const generateThumbnailFromVideo = (url) => {
    return new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';
      video.muted = true;

      const captureFrame = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 160;
          canvas.height = 90;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        } catch (e) {
          resolve(null);
        }
      };

      video.onloadeddata = () => {
        // Seek to 1 second for a meaningful frame
        video.currentTime = Math.min(1, video.duration || 0);
      };
      video.onseeked = captureFrame;
      video.onerror = () => resolve(null);
      setTimeout(() => resolve(null), 20000);
      video.src = url;
    });
  };

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  // Check if current mode is an AI beat mode
  const isAiBeatMode = editMode === "ai_timing" || editMode === "ai_plus" || editMode === "smart_ai" || editMode === "drop_ai";

  // Music is per output. Sneak Peek uses `selectedMusic` (it also drives the beat-mapped cut);
  // each reel tab either follows it (musicMode "shared", default) or carries its own track.
  const tabIsSneak = reelType === "sneak_peek";
  const tabOwnMusic = !tabIsSneak && outputConfig[reelType]?.musicMode === "own";
  const activeMusic = tabOwnMusic ? (outputConfig[reelType]?.music || null) : selectedMusic;
  // Any music action on a reel tab only ever touches that reel (auto-switches it to "Own track");
  // the Sneak Peek track changes only from the Sneak Peek tab.
  const setActiveMusic = (track) => (tabIsSneak ? setSelectedMusic(track) : setOutput(reelType, { musicMode: "own", music: track }));
  const beatPicker = tabIsSneak && isAiBeatMode;
  // Batch payload: null → follow the Sneak Peek track, "" → this reel renders silent
  const outputMusic = (kind) => {
    const cfg = outputConfig[kind] || {};
    if (kind === "sneak_peek" || cfg.musicMode !== "own") return null;
    return cfg.music || "";
  };

  // Reel tabs always show the track list, so make sure it's loaded when one opens
  useEffect(() => {
    if (!tabIsSneak && musicSearchResults.length === 0 && !isSearchingMusic) searchMusic('');
  }, [reelType]); // eslint-disable-line

  // Apply edit mode when changed
  useEffect(() => {
    if (isAiBeatMode) {
      // AI modes: auto-open music panel, load beat-mapped tracks
      setShowMusicPanel(true);
      if (beatMappedTracks.length === 0) {
        fetchBeatMappedTracks();
      }
      // Clear any non-beat-mapped music selection
      if (selectedMusic && !selectedMusic.bpm) {
        setSelectedMusic(null);
      }
      // If a beat-mapped track is already selected, re-apply with new mode's logic
      if (selectedMusic && selectedMusic.bpm) {
        if (editMode === "drop_ai") {
          setTimeout(() => applyDropAIToClips(selectedMusic), 100);
        } else {
          setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
        }
      }
    } else {
      // Leaving AI mode
      if (editMode !== "custom" && uploadedClips.length > 0) {
        applyEditModeToClips(editMode);
      }
    }
  }, [editMode]);

  // Fetch beat-mapped tracks from the backend
  const fetchBeatMappedTracks = async () => {
    setIsLoadingBeatMapped(true);
    try {
      const response = await axios.get(`${API}/music/beat-mapped`);
      if (response.data.success) {
        setBeatMappedTracks(response.data.tracks);
      }
    } catch (error) {
      console.error("Failed to load beat-mapped tracks:", error);
      toast.error("Failed to load AI Timing tracks");
    } finally {
      setIsLoadingBeatMapped(false);
    }
  };

  // Apply beat-map timing to clips
  // AI Timing: strict 2-beat cuts (1-2-CUT, 1-2-CUT)
  // AI+: 1:2:1 repeating pattern — short, long, short (2 beats, 4 beats, 2 beats)
  const applyBeatMapToClips = (track, mode) => {
    if (!track || !track.bpm || uploadedClips.length === 0) return;

    const activeMode = mode || editMode;
    const beatInterval = 60.0 / track.bpm;
    const MIN_START_OFFSET = 2;

    // Build the beat pattern based on mode (units = beats; at 120 BPM, 1 beat = 0.5s, 1 bar = 4 beats = 2s)
    let beatPattern;
    if (activeMode === "ai_plus") {
      // AI+ pattern: short, long, short — repeating cleanly across the timeline.
      // 2 beats, 4 beats, 2 beats (= 1/2 bar, 1 bar, 1/2 bar). Matches the
      // "Mixed 2 & 4 beat flow" label.
      beatPattern = [2, 4, 2];
    } else if (activeMode === "smart_ai") {
      // Smart AI: adaptive pacing — fast intro (grab attention), slow build
      // in the middle (let the track breathe and key shots land), then fast
      // outro (climax / payoff). Curve baked PER-CLIP below.
      beatPattern = null;
    } else {
      // AI Timing: strict 2-beat cuts (half-bar). Matches the "Quick 2-beat
      // cuts" label and gives the energetic gimbal-cut feel on most tracks.
      beatPattern = [2];
    }

    setUploadedClips(prev => {
      // Sort by order to assign beats in stitch/display order
      const orderSorted = prev
        .map((clip, origIdx) => ({ clip, origIdx }))
        .sort((a, b) => a.clip.order - b.clip.order);

      // ── Smart AI: compute a per-clip beat allocation curve ──
      // Strategy: when a track has a curated `rhythm_profile`, we keep the
      // intro and outro exactly as defined (the dramatic edges) and stretch
      // the peak section to absorb any extra clips. Build sits between.
      // This preserves the "feel" of the track regardless of clip count.
      let smartBeats = null;
      if (activeMode === "smart_ai" && orderSorted.length > 0) {
        const n = orderSorted.length;
        const profile = track.rhythm_profile;

        if (profile && (profile.intro || profile.build || profile.peak || profile.outro)) {
          const intro = Array.isArray(profile.intro) ? profile.intro : [];
          const build = Array.isArray(profile.build) ? profile.build : [];
          const peak  = Array.isArray(profile.peak)  ? profile.peak  : [];
          const outro = Array.isArray(profile.outro) ? profile.outro : [];

          const introLen = intro.length;
          const buildLen = build.length;
          const peakLen  = peak.length;
          const outroLen = outro.length;
          const profileLen = introLen + buildLen + peakLen + outroLen;

          if (n >= profileLen) {
            // Fit intro + build + outro exactly; pad peak by repeating the
            // last (longest) hold so dramatic edges stay sharp.
            const peakSlots = n - (introLen + buildLen + outroLen);
            const peakStretched = [];
            for (let i = 0; i < peakSlots; i++) {
              // Cycle through peak values so we can hold longest-then-shorter
              // if the profile gives multiple peak beats (e.g. [8, 6])
              peakStretched.push(peak.length > 0 ? peak[i % peak.length] : 6);
            }
            smartBeats = [...intro, ...build, ...peakStretched, ...outro];
          } else if (n >= introLen + outroLen + 1) {
            // Squeeze: keep intro + outro, fit (build+peak) into the middle slots
            const middleSlots = n - introLen - outroLen;
            const middlePool = [...build, ...peak];
            const middle = [];
            for (let i = 0; i < middleSlots; i++) {
              const idx = Math.round((i / Math.max(1, middleSlots - 1)) * (middlePool.length - 1));
              middle.push(middlePool[idx] ?? 4);
            }
            smartBeats = [...intro, ...middle, ...outro];
          } else {
            // Very few clips — sample evenly across the whole flattened profile
            const flat = [...intro, ...build, ...peak, ...outro];
            smartBeats = [];
            for (let i = 0; i < n; i++) {
              const idx = Math.round((i / Math.max(1, n - 1)) * (flat.length - 1));
              smartBeats.push(flat[idx]);
            }
          }
        } else {
          // Smart AI fallback (no curated profile): "fast intro → slow build → fast outro".
          // Shape:
          //   ~25% of clips up front  = fast cuts   (2 beats each)
          //   ~50% of clips middle    = slow build  (ramping 4 → 8 → 6 beats)
          //   ~25% of clips at end    = fast finish (2 beats each)
          //
          // This produces a strong social piece: punchy hook, breathable middle
          // where the property shots can land, then a snappy payoff.
          const introPortion = 0.25;
          const outroPortion = 0.25;
          const introCount = n <= 3 ? 1 : Math.max(2, Math.round(n * introPortion));
          const outroCount = n <= 3 ? 1 : Math.max(2, Math.round(n * outroPortion));
          const buildCount = Math.max(1, n - introCount - outroCount);

          smartBeats = [];

          // Fast intro: 2 beats per clip (half-bar hits, like AI Timing)
          for (let i = 0; i < introCount; i++) {
            smartBeats.push(2);
          }

          // Slow build: ramp from 4 beats → 8 beats → settle to 6 beats.
          // We use a smooth easing curve so cuts gradually slow, peak in
          // the middle, then start nudging back up toward the outro.
          for (let i = 0; i < buildCount; i++) {
            const t = buildCount > 1 ? i / (buildCount - 1) : 0.5;
            // Bell-ish curve peaking at t=0.5 (the slowest cut sits centre)
            const slowness = 1 - Math.abs(t - 0.5) * 2; // 0 at edges, 1 at centre
            // Map 0→1 onto 4 beats → 8 beats
            const beats = 4 + slowness * 4;
            // Quantize to half-beats so we sit on the grid (0.5 of a beat ≈ 0.25s at 120 BPM)
            smartBeats.push(Math.round(beats * 2) / 2);
          }

          // Fast finish: 2 beats per clip (mirror the intro)
          for (let i = 0; i < outroCount; i++) {
            smartBeats.push(2);
          }
        }
      }

      // Assign beat pattern by stitch order position
      const updates = {};
      orderSorted.forEach((entry, stitchPos) => {
        const clip = entry.clip;
        // Trust real probed duration above all else; if missing, use a
        // conservative 6s fallback (median iPhone property clip length) so
        // trims never seek into time that doesn't exist.
        const rawDuration = Number(clip.duration);
        const fullDuration = (rawDuration && rawDuration > 0.5) ? rawDuration : 6;
        const beats = smartBeats
          ? smartBeats[stitchPos]
          : beatPattern[stitchPos % beatPattern.length];

        // Want this many seconds of footage in the stitched output
        const desiredDuration = beats * beatInterval;

        // How much of the clip is usable (skip the 2s lead-in, but never
        // require more than the clip itself).
        const effectiveStartOffset = Math.min(MIN_START_OFFSET, Math.max(0, fullDuration * 0.2));
        const usable = Math.max(0.5, fullDuration - effectiveStartOffset);
        const actualDuration = Math.min(desiredDuration, usable);

        // CENTER of the clip — gimbal/slider movement is always in the middle.
        // But clamp so [startTime, endTime] never escapes [effectiveStartOffset, fullDuration].
        const midPoint = fullDuration / 2;
        const halfTarget = actualDuration / 2;
        let startTime = Math.max(effectiveStartOffset, midPoint - halfTarget);
        let endTime = Math.min(fullDuration, startTime + actualDuration);
        // If clamping endTime shortened us, shift the start back to preserve duration when we can
        if ((endTime - startTime) < actualDuration && startTime > effectiveStartOffset) {
          startTime = Math.max(effectiveStartOffset, endTime - actualDuration);
        }

        updates[entry.origIdx] = { startTime, endTime };
      });

      return prev.map((clip, idx) => ({
        ...clip,
        startTime: updates[idx].startTime,
        endTime: updates[idx].endTime,
      }));
    });

    if (activeMode === "ai_plus") {
      const short = (2 * beatInterval).toFixed(2);
      const long = (4 * beatInterval).toFixed(2);
      toast.success(`AI+ — ${track.bpm} BPM, pattern: ${short}s / ${long}s / ${short}s (2-4-2 beats)`);
    } else if (activeMode === "smart_ai") {
      const hasProfile = track.rhythm_profile && Object.keys(track.rhythm_profile).length > 0;
      toast.success(
        hasProfile
          ? `Smart AI — track-specific rhythm profile applied (${track.bpm} BPM)`
          : `Smart AI — fast intro · slow build · fast finish (${track.bpm} BPM)`
      );
    } else {
      const dur2beat = (2 * beatInterval).toFixed(2);
      toast.success(`AI Timing — ${track.bpm} BPM, ${dur2beat}s per clip (2-beat cuts)`);
    }
  };

  // Drop AI: Featured (starred) clips placed at drop points.
  // Uses actual Cyanite drop timestamps when available, otherwise estimates from BPM + energy.
  const applyDropAIToClips = (track) => {
    if (!track || !track.bpm || uploadedClips.length === 0) return;

    const beatInterval = 60.0 / track.bpm;
    const barDuration = 4 * beatInterval;
    const MIN_START_OFFSET = 2;
    const dropDuration = barDuration * 2; // Featured clips get 2 bars at the drop
    const normalDuration = barDuration;    // Normal clips get 1 bar

    // Determine drop positions (as clip-index positions in the sequence)
    const hasRealDrops = track.drops && track.drops.length > 0;
    let dropPositions;

    if (hasRealDrops) {
      // Convert drop timestamps to clip-index positions.
      // Each normal clip occupies ~1 bar of time, so position = dropTime / barDuration
      dropPositions = new Set(
        track.drops.map(t => Math.round(t / barDuration)).filter(p => p > 0)
      );
    } else {
      // Estimate: drops every 8 or 16 bars based on energy
      const energy = track.energy || 0.5;
      const dropBarInterval = energy >= 0.6 ? 8 : 16;
      dropPositions = new Set();
      for (let p = dropBarInterval; p < uploadedClips.length + 20; p += dropBarInterval) {
        dropPositions.add(p);
      }
    }

    setUploadedClips(prev => {
      const orderSorted = prev
        .map((clip, origIdx) => ({ clip, origIdx }))
        .sort((a, b) => a.clip.order - b.clip.order);

      const starredQueue = orderSorted.filter(e => e.clip.isStarred);
      const normalQueue = orderSorted.filter(e => !e.clip.isStarred);

      const sequence = [];
      let starIdx = 0;
      let normalIdx = 0;
      const totalClips = orderSorted.length;

      for (let pos = 0; pos < totalClips; pos++) {
        const isDropPosition = dropPositions.has(pos);

        if (isDropPosition && starIdx < starredQueue.length) {
          sequence.push({ entry: starredQueue[starIdx], isDrop: true });
          starIdx++;
        } else if (normalIdx < normalQueue.length) {
          sequence.push({ entry: normalQueue[normalIdx], isDrop: false });
          normalIdx++;
        } else if (starIdx < starredQueue.length) {
          sequence.push({ entry: starredQueue[starIdx], isDrop: true });
          starIdx++;
        }
      }

      // If no clips are starred, put the first clip as a drop hit
      if (starredQueue.length === 0 && sequence.length > 0) {
        sequence[0].isDrop = true;
      }

      const updates = {};
      sequence.forEach(({ entry, isDrop }) => {
        const clip = entry.clip;
        const rawDuration = Number(clip.duration);
        const fullDuration = (rawDuration && rawDuration > 0.5) ? rawDuration : 6;
        const targetDur = isDrop ? dropDuration : normalDuration;
        const effectiveOffset = Math.min(MIN_START_OFFSET, Math.max(0, fullDuration * 0.2));
        const usable = Math.max(0.5, fullDuration - effectiveOffset);
        const actualDuration = Math.min(targetDur, usable);

        const midPoint = fullDuration / 2;
        const halfTarget = actualDuration / 2;
        let startTime = Math.max(effectiveOffset, midPoint - halfTarget);
        let endTime = Math.min(fullDuration, startTime + actualDuration);
        if ((endTime - startTime) < actualDuration && startTime > effectiveOffset) {
          startTime = Math.max(effectiveOffset, endTime - actualDuration);
        }

        updates[entry.origIdx] = { startTime, endTime };
      });

      return prev.map((clip, idx) => ({
        ...clip,
        startTime: updates[idx]?.startTime ?? clip.startTime,
        endTime: updates[idx]?.endTime ?? clip.endTime,
      }));
    });

    const starCount = uploadedClips.filter(c => c.isStarred).length;
    const dropSource = hasRealDrops ? `${track.drops.length} Cyanite drops` : 'estimated drops';
    toast.success(`Drop AI — ${track.bpm} BPM, ${starCount > 0 ? `${starCount} featured clip${starCount > 1 ? 's' : ''} hit the drops` : 'star clips to place them at drops'} (${dropSource})`);
  };

  const applyEditModeToClips = (mode) => {
    if (mode === "custom" || mode === "ai_timing" || mode === "ai_plus" || mode === "smart_ai" || mode === "drop_ai") return;

    // Define duration ranges for each mode
    let minDuration, maxDuration;
    if (mode === "default") {
      minDuration = 1.4;
      maxDuration = 1.4;
    } else if (mode === "short") {
      minDuration = 3;
      maxDuration = 5;
    } else {
      // long mode
      minDuration = 5;
      maxDuration = 10;
    }

    // Never use the first 2 seconds of any clip (camera shake, transitions, etc.)
    // For short clips, scale this offset down so we never demand more lead-in than the
    // clip has spare (otherwise the trim window collapses or seeks past the end).
    const BASE_START_OFFSET = 2;

    // Alternating start positions for default mode (14s, 2s, 14s, 2s...)
    const defaultStartPositions = [14, 2];

    setUploadedClips(prev => prev.map((clip, index) => {
      // Trust the backend-probed duration. If it's missing (failed probe), use a
      // conservative 6s fallback rather than the old 25s default that caused
      // trim windows to seek beyond the end of real iPhone clips.
      const rawDuration = Number(clip.duration);
      const clipDuration = (rawDuration && rawDuration > 0.5) ? rawDuration : 6;

      // Skip lead-in, but never demand more than 20% of the clip
      const effectiveOffset = Math.min(BASE_START_OFFSET, Math.max(0, clipDuration * 0.2));
      const usableDuration = Math.max(0.5, clipDuration - effectiveOffset);
      const targetDuration = Math.min(
        minDuration + Math.random() * (maxDuration - minDuration),
        usableDuration
      );

      let startTime, endTime;

      if (mode === "default") {
        // Alternate between 14s and 2s start positions
        const preferredStart = defaultStartPositions[index % 2];

        if (clipDuration >= preferredStart + targetDuration) {
          startTime = preferredStart;
          endTime = preferredStart + targetDuration;
        } else {
          // Clip too short for preferred start — center within usable range
          const midPoint = clipDuration / 2;
          startTime = Math.max(effectiveOffset, midPoint - targetDuration / 2);
          endTime = Math.min(clipDuration, startTime + targetDuration);
        }
      } else {
        // Short/Long mode - use middle portion of the clip, but never before 2s
        const midPoint = clipDuration / 2;
        const halfTarget = targetDuration / 2;
        startTime = Math.max(effectiveOffset, midPoint - halfTarget);
        endTime = Math.min(clipDuration, startTime + targetDuration);
        // If clamping shortened us, shift start back to preserve duration
        if ((endTime - startTime) < targetDuration && startTime > effectiveOffset) {
          startTime = Math.max(effectiveOffset, endTime - targetDuration);
        }
      }

      return {
        ...clip,
        startTime,
        endTime,
      };
    }));

    // Create descriptive toast message
    const modeLabels = {
      default: "Sneak Peek (1.4s clips, alternating start)",
      short: "Short Edit (3-5s per clip)",
      long: "Long Edit (5-10s per clip)"
    };
    toast.success(`Applied ${modeLabels[mode]}`);
  };


  const loadCompanyLogo = async () => {
    try {
      const response = await axios.get(`${API}/company/logo`);
      if (response.data.success && response.data.logo) {
        setCompanyLogo(response.data.logo);
      }
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  };

  // Handle video upload via Cloudinary (for manual file uploads)
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (uploadedClips.length + files.length > 20) {
      toast.error("Maximum 20 clips allowed");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append("files", file));

      const response = await axios.post(`${API}/video/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent === 100 ? 99 : percent);
        }
      });

      setUploadProgress(100);
      
      if (response.data.success) {
        const newClips = response.data.uploaded.map((clip, idx) => ({
          ...clip,
          sourceType: 'local', // Mark as Cloudinary upload
          includeInStitch: true,
          isStarred: false,
          startTime: null,
          endTime: null,
          order: uploadedClips.length + idx
        }));
        
        setUploadedClips(prev => {
          const updated = [...prev, ...newClips];
          // Apply timing: for AI beat modes, re-apply beat map; otherwise apply edit mode
          if (editMode === "drop_ai" && selectedMusic) {
            setTimeout(() => applyDropAIToClips(selectedMusic), 100);
          } else if ((editMode === "ai_timing" || editMode === "ai_plus" || editMode === "smart_ai") && selectedMusic) {
            setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
          } else if (editMode !== "custom") {
            setTimeout(() => applyEditModeToClips(editMode), 100);
          }
          return updated;
        });
        toast.success(`${response.data.total_uploaded} clip(s) uploaded`);
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`Failed: ${err.filename}`);
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload videos");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (fileOrEvent) => {
    // Handle both file and event
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;

    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const endpoint = wmsClient?.id
        ? `${API}/company/client-logo/${wmsClient.id}/upload`
        : `${API}/company/logo`;
      if (wmsClient?.id) formData.append("client_name", wmsClient.name || "");

      const response = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setCompanyLogo({
          public_id: response.data.public_id,
          url: response.data.url,
          secure_url: response.data.secure_url
        });
        toast.success(wmsClient?.id ? `Logo saved for ${wmsClient.name || "this client"}` : "Logo uploaded");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Music search — queries R2 track library
  const searchMusic = async (query, vibe = null) => {
    setIsSearchingMusic(true);
    try {
      const params = { page_size: 30 };
      if (query && query.trim()) params.query = query.trim();
      if (vibe) params.vibe = vibe;

      const response = await axios.get(`${API}/music/search`, { params });

      if (response.data.success) {
        setMusicSearchResults(response.data.tracks);
        if (response.data.vibes) setMusicVibes(response.data.vibes);
        if (response.data.tracks.length === 0) {
          toast.info("No tracks found. Try different keywords.");
        }
      } else {
        toast.error("Failed to load music library");
        setMusicSearchResults([]);
      }
    } catch (error) {
      console.error("Music search error:", error);
      toast.error("Failed to load music library");
      setMusicSearchResults([]);
    } finally {
      setIsSearchingMusic(false);
    }
  };

  // Music preview — streams from R2, only one track at a time
  const handleMusicPreview = (track) => {
    setTrackLoadError(null);
    if (previewingTrackId === track.id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPreviewingTrackId(null);
    } else {
      if (!track.url) {
        toast.error("Track URL not available");
        return;
      }
      // Start playing new track (stops any current playback)
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = track.url;
        audioRef.current.load();
        
        // Wait for enough data to buffer before playing
        const onCanPlay = () => {
          audioRef.current.removeEventListener('canplay', onCanPlay);
          audioRef.current.removeEventListener('error', onError);
          audioRef.current.play().catch((err) => {
            console.error("Audio play error:", err);
            setTrackLoadError(track.id);
            setPreviewingTrackId(null);
            toast.error("Could not play track");
          });
        };
        const onError = (e) => {
          audioRef.current.removeEventListener('canplay', onCanPlay);
          audioRef.current.removeEventListener('error', onError);
          console.error("Audio load error:", e);
          setTrackLoadError(track.id);
          setPreviewingTrackId(null);
          toast.error("Failed to load track audio");
        };
        audioRef.current.addEventListener('canplay', onCanPlay);
        audioRef.current.addEventListener('error', onError);
      }
      setPreviewingTrackId(track.id);
    }
  };

  // Handle audio ended / error
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleEnded = () => setPreviewingTrackId(null);
    const handleError = () => {
      setPreviewingTrackId((id) => { if (id) setTrackLoadError(id); return null; });
    };
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  // Select music track — for the tab in view (Sneak Peek track, or a reel's own track)
  const handleSelectMusic = (track) => {
    // Stop any playing preview
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPreviewingTrackId(null);

    if (activeMusic?.id === track.id) {
      setActiveMusic(null);
      toast.info(tabIsSneak ? "Music track removed" : `${OUTPUT_KINDS.find((k) => k.value === reelType)?.label || "This reel"} will render without music`);
    } else {
      // Store full track object for the video rendering pipeline
      setActiveMusic(track);
      toast.success(!tabIsSneak
        ? `${track.name} — ${OUTPUT_KINDS.find((k) => k.value === reelType)?.label || "this reel"} only`
        : `Selected: ${track.name}`);
      
      // If AI beat mode, apply beat-map timing when track is selected (Sneak Peek cut only)
      if (tabIsSneak && isAiBeatMode && track.bpm) {
        if (editMode === "drop_ai") {
          setTimeout(() => applyDropAIToClips(track), 100);
        } else {
          setTimeout(() => applyBeatMapToClips(track, editMode), 100);
        }
      }
    }
  };

  // Toggle stitch inclusion
  // Toggle star (feature clip) - max 3 starred
  const toggleStar = (clipId) => {
    setUploadedClips(prev => {
      const clip = prev.find(c => c.id === clipId);
      if (!clip) return prev;
      
      // If already starred, unstar it
      if (clip.isStarred) {
        return prev.map(c => c.id === clipId ? { ...c, isStarred: false } : c);
      }
      
      // Check if we already have 3 starred
      const starredCount = prev.filter(c => c.isStarred).length;
      if (starredCount >= 3) {
        toast.error("Maximum 3 feature clips allowed");
        return prev;
      }
      
      return prev.map(c => c.id === clipId ? { ...c, isStarred: true } : c);
    });
    // Re-apply Drop AI timing when stars change
    if (editMode === "drop_ai" && selectedMusic) {
      setTimeout(() => applyDropAIToClips(selectedMusic), 150);
    }
  };

  // Update clip trim times from preview slider
  const handleTrimChange = (clipId, startTime, endTime) => {
    setUploadedClips(prev => prev.map(clip =>
      clip.id === clipId ? { 
        ...clip, 
        startTime: startTime === 0 ? null : startTime,
        endTime: endTime === clip.duration ? null : endTime
      } : clip
    ));
    // Switch to custom mode if user manually adjusts
    if (editMode !== "custom") {
      setEditMode("custom");
    }
  };

  // Update clip trim times (legacy support)
  const updateClipTrim = (clipId, field, value) => {
    let numValue = null;
    if (value !== null && value !== undefined && value !== "") {
      const parsed = parseFloat(value);
      numValue = isNaN(parsed) ? null : parsed;
    }
    setUploadedClips(prev => prev.map(clip =>
      clip.id === clipId ? { ...clip, [field]: numValue } : clip
    ));
  };

  // Remove clip - handles both R2 and legacy Cloudinary clips
  const removeClip = async (clip) => {
    const clipId = clip.id;
    setUploadedClips(prev => prev.filter(c => c.id !== clipId));
    setPreviewClip(prev => (prev && prev.id === clipId ? null : prev));
    
    try {
      if (clip.sourceType === 'r2') {
        // R2 clips live in user's bucket — just remove from local state
      } else {
        // Legacy Cloudinary delete
        const publicId = clip.public_id;
        if (publicId) {
          await axios.delete(`${API}/video/clip/${encodeURIComponent(publicId)}`);
        }
      }
    } catch (error) {
      console.error("Error deleting clip:", error);
    }
  };

  // Reverse clip order — remaps order values so both the visible list and
  // the stitched output sequence flip. Unlike a display-only toggle, this
  // mutates the source of truth so downstream generation matches the UI.
  const reverseClipOrder = () => {
    setUploadedClips(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const total = sorted.length;
      const remapped = new Map(sorted.map((c, i) => [c.id, total - 1 - i]));
      return prev.map(c => ({ ...c, order: remapped.get(c.id) ?? c.order }));
    });
  };

  // DnD Kit drag end handler for clip reorder
  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sortedClips = [...uploadedClips].sort((a, b) => a.order - b.order);
    const oldIndex = sortedClips.findIndex(c => c.id === active.id);
    const newIndex = sortedClips.findIndex(c => c.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const reorderedIds = arrayMove(
      sortedClips.map(c => c.id),
      oldIndex,
      newIndex
    );
    
    setUploadedClips(prev => prev.map(clip => {
      const newOrderIdx = reorderedIds.indexOf(clip.id);
      return { ...clip, order: newOrderIdx };
    }));
    // Re-apply beat map after reorder if in AI mode
    if (editMode === "drop_ai" && selectedMusic) {
      setTimeout(() => applyDropAIToClips(selectedMusic), 100);
    } else if ((editMode === "ai_timing" || editMode === "ai_plus") && selectedMusic) {
      setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
    }
  };

  // Toggle format selection
  const toggleFormat = (formatKey) => {
    setSelectedFormats(prev => {
      if (prev.includes(formatKey)) {
        if (prev.length === 1) {
          toast.error("At least one format must be selected");
          return prev;
        }
        return prev.filter(f => f !== formatKey);
      }
      return [...prev, formatKey];
    });
  };

  // Move clip up or down in order
  const moveClip = (clipId, direction) => {
    const sortedClips = [...uploadedClips].sort((a, b) => a.order - b.order);
    const currentIdx = sortedClips.findIndex(c => c.id === clipId);
    if (currentIdx === -1) return;
    
    const newIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
    if (newIdx < 0 || newIdx >= sortedClips.length) return;
    
    const currentClipId = sortedClips[currentIdx].id;
    const swapClipId = sortedClips[newIdx].id;
    
    setUploadedClips(prev => prev.map(clip => {
      if (clip.id === currentClipId) return { ...clip, order: sortedClips[newIdx].order };
      if (clip.id === swapClipId) return { ...clip, order: sortedClips[currentIdx].order };
      return clip;
    }));
    // Re-apply beat map after move if in AI mode
    if (editMode === "drop_ai" && selectedMusic) {
      setTimeout(() => applyDropAIToClips(selectedMusic), 100);
    } else if ((editMode === "ai_timing" || editMode === "ai_plus") && selectedMusic) {
      setTimeout(() => applyBeatMapToClips(selectedMusic, editMode), 100);
    }
  };

  // ── Smart Select: vision-tag every clip, keep the best N in house-tour order ──
  const runSmartSelect = async (target = smartSelect.target, clipsArg) => {
    const clips = clipsArg || uploadedClips;
    if (clips.length === 0) return;
    const numericTarget = target === "all" ? clips.length : target;
    setSmartSelect((st) => ({ ...st, running: true, target, error: null }));
    try {
      const res = await axios.post(`${API}/video/smart-select`, {
        clips: clips.map((c) => ({ public_id: c.public_id, url: c.r2Url || c.secure_url || c.url, duration: c.duration || null })),
        target: numericTarget,
      }, { timeout: 180000 });
      const byPid = Object.fromEntries((res.data.clips || []).map((r) => [r.public_id, r]));
      setUploadedClips((prev) => prev.map((c) => {
        const r = byPid[c.public_id];
        if (!r) return c;
        return {
          ...c,
          order: r.order,
          includeInEdit: r.selected,
          skipReason: r.selected ? null : r.reason,
          room: { category: r.category, label: r.label, categoryLabel: r.category_label, quality: r.quality },
        };
      }));
      const summary = res.data.summary || {};
      setSmartSelect({ running: false, target, applied: true, summary, error: null });
      setTimeout(() => {
        if (isAiBeatMode && selectedMusicRef.current) applyBeatMapToClips(selectedMusicRef.current, editMode);
        else applyEditModeToClips(editMode);
      }, 60);
      const rooms = (summary.rooms || []).map((r) => r.label.toLowerCase()).slice(0, 4).join(", ");
      toast.success(`Smart select: ${summary.selected} of ${summary.analysed} clips picked${rooms ? ` — ${rooms}…` : ""}`, { duration: 6000 });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setSmartSelect((st) => ({ ...st, running: false, error: detail || "failed" }));
      toast.error(typeof detail === "string" ? detail : "Smart select couldn't analyse the clips");
    }
  };

  const useAllClips = () => {
    setUploadedClips((prev) => prev.map((c) => ({ ...c, includeInEdit: true, skipReason: null })));
    setSmartSelect((st) => ({ ...st, applied: false, target: "all" }));
    setTimeout(() => applyEditModeToClips(editMode), 60);
  };

  // Include/skip a single clip. Re-included clips slot back in by room order (smart) or keep their place.
  const toggleIncludeClip = (clipId) => {
    setUploadedClips((prev) => {
      const target = prev.find((c) => c.id === clipId);
      if (!target) return prev;
      const nowIncluded = target.includeInEdit === false;
      const rest = prev.filter((c) => c.id !== clipId).sort((a, b) => a.order - b.order);
      const updated = { ...target, includeInEdit: nowIncluded, skipReason: nowIncluded ? null : "Skipped by you" };
      const included = rest.filter((c) => c.includeInEdit !== false);
      const excluded = rest.filter((c) => c.includeInEdit === false);
      let seq;
      if (nowIncluded) {
        const catIdx = (c) => ROOM_ORDER.indexOf(c.room?.category ?? "other");
        let at = included.length;
        if (updated.room) {
          at = included.findIndex((c) => catIdx(c) > catIdx(updated));
          if (at === -1) at = included.length;
        }
        seq = [...included.slice(0, at), updated, ...included.slice(at), ...excluded];
      } else {
        seq = [...included, updated, ...excluded];
      }
      return seq.map((c, i) => ({ ...c, order: i }));
    });
    setTimeout(() => applyEditModeToClips(editMode), 60);
  };

  // Full builder snapshot stored with every render so it can be reopened and re-rendered later.
  const buildEditState = (music) => ({
    v: 1,
    clips: uploadedClips.map(({ file, blob, ...c }) => c),
    editMode,
    selectedMusic: music || null,
    logo: { enabled: logoEnabled, position: logoPosition, scale: logoScale, opacity: logoOpacity, companyLogo },
    selectedFormats,
    outputConfig,
    propertyDetails,
    wmsJobId: wmsJobId || null,
    wmsClient: wmsClient || null,
    smartSelect: { applied: smartSelect.applied, target: smartSelect.target },
  });

  const restoreEditState = (state, meta) => {
    if (!state || !Array.isArray(state.clips)) { toast.error("This render has no saved edit"); return; }
    setBatch(null);
    setHistoryOpen(false);
    setEditMode(state.editMode || "default");
    if (state.selectedMusic) { setSelectedMusic(state.selectedMusic); selectedMusicRef.current = state.selectedMusic; }
    else { setSelectedMusic(null); selectedMusicRef.current = null; }
    if (state.logo) {
      setLogoEnabled(!!state.logo.enabled);
      setLogoPosition(state.logo.position || "north");
      setLogoScale(state.logo.scale ?? 60);
      setLogoOpacity(state.logo.opacity ?? 100);
      setCompanyLogo(state.logo.companyLogo || null);
    }
    if (Array.isArray(state.selectedFormats) && state.selectedFormats.length) setSelectedFormats(state.selectedFormats);
    if (state.outputConfig) setOutputConfig((prev) => ({ ...prev, ...state.outputConfig }));
    if (state.propertyDetails) setPropertyDetails({ name: "", location: "", old_price: "", new_price: "", ...state.propertyDetails });
    if (state.wmsJobId) setWmsJobId(String(state.wmsJobId));
    if (state.wmsClient) setWmsClient(state.wmsClient);
    setSmartSelect({ running: false, target: state.smartSelect?.target || 12, applied: !!state.smartSelect?.applied, summary: null });
    // clips last, after the edit-mode effect has run, so the saved trims survive
    setTimeout(() => setUploadedClips(state.clips.map((c) => ({ ...c }))), 200);
    setParentBatch({ id: meta.batch_id, version: meta.version || 1 });
    toast.success(`Edit restored (v${meta.version || 1}) — tweak anything, then Generate to render v${(meta.version || 1) + 1}`, { duration: 7000 });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const openBatchOutputs = async (b) => {
    try {
      const r = await axios.get(`${API}/video/creatomate/batches/${b.batch_id}`);
      setBatch(r.data);
      setSubmittedVideos({});
      setHubPushed({});
      setHistoryOpen(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open that render");
    }
  };

  const reopenBatchEdit = async (b) => {
    try {
      const r = await axios.get(`${API}/video/creatomate/batches/${b.batch_id}/edit-state`);
      restoreEditState(r.data.edit_state, { batch_id: b.batch_id, version: r.data.version });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reopen that edit");
    }
  };

  // ── Generate: Creatomate batch — Sneak Peek first, then every enabled reel ──
  // Editor logins get Sneak Peek only for now — the Just Listed / Price Reduction / Recently Sold reels are locked.
  // Head of Media (editor + editor_admin) has them unlocked.
  const reelsLocked = user?.role === "editor" && !user?.editor_admin;
  const enabledOutputs = OUTPUT_KINDS.filter((k) => k.always || (!reelsLocked && outputConfig[k.value]?.enabled));
  const activeClips = [...uploadedClips].filter((c) => c.includeInEdit !== false).sort((a, b) => a.order - b.order);

  const handleGenerate = async () => {
    if (uploadedClips.length === 0) {
      toast.error("Please upload at least one video clip");
      return;
    }
    if (activeClips.length < 2) {
      toast.error("Need at least 2 included clips for a social video");
      return;
    }
    const reels = enabledOutputs.filter((k) => !k.always);
    if (reels.length > 0 && !propertyDetails.name.trim()) {
      toast.error("Add the property name for the reels");
      setReelType(reels[0].value);
      return;
    }
    if (outputConfig.price_reduction?.enabled && !(propertyDetails.old_price.trim() && propertyDetails.new_price.trim())) {
      toast.error("Price Reduction needs the old and new price");
      setReelType("price_reduction");
      return;
    }

    const currentMusic = selectedMusicRef.current;
    setBatchSubmitting(true);
    try {
      const sortedClips = activeClips;
      const payload = {
        edit_state: buildEditState(currentMusic),
        parent_batch_id: parentBatch?.id || null,
        wms_client: wmsClient ? { id: wmsClient.id, name: wmsClient.name } : null,
        clips: sortedClips.map((clip, idx) => ({
          public_id: clip.public_id,
          include_in_stitch: true,
          is_featured: clip.isStarred || false,
          start_time: clip.startTime,
          end_time: clip.endTime,
          duration: clip.duration || null,
          order: idx,
          source_type: clip.sourceType || 'local',
          r2_key: clip.r2Key || null,
          r2_url: clip.r2Url || null
        })),
        outputs: enabledOutputs.map((k) => {
          const own = outputMusic(k.value);
          return {
            kind: k.value,
            template: outputConfig[k.value]?.template || (k.always ? "plain" : "master"),
            music_track_url: own === null ? null : (own?.url || ""),
            music_track_name: own === null ? null : (own?.name || null),
          };
        }),
        property: {
          name: propertyDetails.name.trim(),
          location: propertyDetails.location.trim(),
          old_price: propertyDetails.old_price.trim(),
          new_price: propertyDetails.new_price.trim(),
        },
        selected_formats: selectedFormats,
        logo_enabled: logoEnabled && !!companyLogo && !companyLogo.broken,
        logo_public_id: logoEnabled && companyLogo && !companyLogo.broken ? companyLogo.public_id : null,
        logo_url: logoEnabled && companyLogo && !companyLogo.broken ? (companyLogo.secure_url || companyLogo.url || null) : null,
        logo_position: logoPosition,
        logo_scale: logoScale,
        logo_opacity: logoOpacity,
        music_track_url: currentMusic?.url || null,
        music_track_name: currentMusic?.name || null,
        wms_job_id: wmsJobId || null,
        edit_mode: editMode,
      };
      const res = await axios.post(`${API}/video/creatomate/batch`, payload, { timeout: 180000 });
      setBatch(res.data);
      setSubmittedVideos({});
      setHubPushed({});
      setParentBatch(null);
      const n = res.data.jobs?.length || 0;
      toast.success(`Preparing ${n} social output${n === 1 ? "" : "s"}${res.data.version > 1 ? ` · v${res.data.version}` : ""} — matching colours, then rendering…`);
      (res.data.warnings || []).forEach((w) => toast.warning(w, { duration: 10000 }));
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to start rendering");
    } finally {
      setBatchSubmitting(false);
    }
  };

  // ── PARKED: legacy Cloudinary renderer (/api/video/process). Kept intact for re-enabling. ──
  // eslint-disable-next-line no-unused-vars
  const handleGenerateLegacy = async () => {
    if (uploadedClips.length === 0) {
      toast.error("Please upload at least one video clip");
      return;
    }

    if (selectedFormats.length === 0) {
      toast.error("Please select at least one output format");
      return;
    }

    const starredClips = uploadedClips.filter(c => c.isStarred);
    
    if (stitchEnabled && uploadedClips.length < 2) {
      toast.error("Need at least 2 clips for stitching");
      return;
    }
    
    if (!stitchEnabled && starredClips.length === 0) {
      toast.error("Star at least one clip to export, or enable stitching");
      return;
    }

    if (reelType !== "sneak_peek") {
      if (!stitchEnabled) {
        toast.error("Reels need stitching enabled");
        return;
      }
      if (!reelTexts.line1.trim()) {
        toast.error(reelType === "price_reduction" ? "Add the old price" : "Add the property headline");
        return;
      }
      if (reelType === "price_reduction" && !reelTexts.line2.trim()) {
        toast.error("Add the new price");
        return;
      }
    }

    // Soft cap — Cloudinary's per-render budget gets unstable past ~20 clips.
    // Hint the user to pre-cull before we let them hit Generate with all 49.
    const includedCount = uploadedClips.filter(c => c.include_in_stitch !== false).length;
    if (stitchEnabled && includedCount > 25 && !window.__nicheStitchManyConfirmed) {
      const ok = window.confirm(
        `You have ${includedCount} clips selected. The cloud renderer works best with ` +
        `12-20 clips per video. Continue anyway? ` +
        `\n\nTip: untick or remove clips you don't want, or star your best ones first.`
      );
      if (!ok) return;
      window.__nicheStitchManyConfirmed = true;
    }

    const currentMusic = selectedMusicRef.current;

    setIsProcessing(true);
    setProcessingStep(0);
    setProcessingMessage("Analysing video clips...");
    setGeneratedVideos(null);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    const processingSteps = [
      "Analysing video clips...",
      "Applying transformations...",
      "Resizing to social formats...",
      stitchEnabled ? "Compiling stitched video..." : "Processing individual clips...",
      currentMusic ? "Adding background music..." : "Optimising quality...",
      "Rendering final outputs...",
      "Almost there..."
    ];

    const messageInterval = setInterval(() => {
      setProcessingStep(prev => {
        const next = prev + 1;
        if (next < processingSteps.length) {
          setProcessingMessage(processingSteps[next]);
        }
        return next;
      });
    }, 400);

    try {
      // Check if any property overlays have content
      const fontSizeMap = { small: 36, medium: 48, large: 64, xlarge: 80 };
      const activePropertyOverlays = Object.entries(propertyOverlays)
        .filter(([_, config]) => config.enabled && config.value)
        .reduce((acc, [key, config]) => {
          acc[key] = {
            text: config.value,
            font_size: fontSizeMap[config.fontSize] || 48,
            position: config.position,
            font: config.font,
            color: config.color,
            text_shadow: config.textShadow !== false
          };
          return acc;
        }, {});
      
      const hasPropertyOverlays = Object.keys(activePropertyOverlays).length > 0;

      const sortedClips = [...uploadedClips].sort((a, b) => a.order - b.order);

      const requestData = {
        clips: sortedClips.map((clip, idx) => ({
          public_id: clip.public_id,
          include_in_stitch: stitchEnabled,
          is_featured: clip.isStarred || false,
          start_time: clip.startTime,
          end_time: clip.endTime,
          order: idx,
          source_type: clip.sourceType || 'local',
          r2_key: clip.r2Key || null,
          r2_url: clip.r2Url || null
        })),
        selected_formats: selectedFormats,
        crop_mode: cropMode,
        feature_clip_duration: 12,
        logo_enabled: logoEnabled && companyLogo !== null,
        logo_public_id: logoEnabled ? companyLogo?.public_id : null,
        logo_position: logoPosition,
        logo_scale: logoScale,
        logo_padding: logoPadding,
        logo_opacity: logoOpacity,
        stitch_enabled: stitchEnabled,
        stitch_transition: stitchTransition,
        // Music info - use ref to ensure we have the current value
        music_track_url: currentMusic?.url || null,
        music_track_id: currentMusic?.id || null,
        // Property overlays - send enabled overlays with values
        property_overlays_enabled: hasPropertyOverlays,
        property_overlays: hasPropertyOverlays ? activePropertyOverlays : null,
        // Group settings for text overlays (matching preview)
        overlay_group_position: sharedPosition,
        overlay_group_background: groupBackground,
        // Reel title sequence
        reel_overlay: reelType !== "sneak_peek" ? {
          reel_type: reelType,
          line1: reelTexts.line1.trim(),
          line2: reelTexts.line2.trim(),
        } : null
      };
      
      console.log("Sending video process request:", {
        ...requestData,
        music_track_url: requestData.music_track_url ? requestData.music_track_url.substring(0, 50) + "..." : null,
        currentMusic: currentMusic ? { id: currentMusic.id, title: currentMusic.title } : null
      });
      
      const response = await axios.post(`${API}/video/process`, requestData, {
        timeout: 300000
      });

      console.log("Video process response:", response.data);
      
      if (response.data && response.data.success) {
        let message = response.data.message || `Generated ${response.data.total_count} videos`;
        message = message.replace(/cloudinary/gi, '').replace(/\s+/g, ' ').trim();
        
        const videoData = {
          ...response.data,
          individual_outputs: response.data.individual_outputs || {},
          stitched_outputs: response.data.stitched_outputs || [],
          total_count: response.data.total_count || 0
        };
        
        console.log("Setting generated videos:", videoData);
        setGeneratedVideos(videoData);
        toast.success(message);
        
        // Show music warning if the track was unavailable, or if the stitch
        // had to be skipped (e.g. clip pre-upload failure on huge property packs).
        if (response.data.music_warning) {
          toast.warning(response.data.music_warning, { duration: 12000 });
        }
        if (response.data.logo_warning) {
          toast.warning(response.data.logo_warning, { duration: 12000 });
        }
        
        // If some videos aren't ready yet, keep the loader and poll
        const allOutputs = [
          ...(videoData.stitched_outputs || []),
          ...Object.values(videoData.individual_outputs || {}).flat()
        ];
        const notReady = allOutputs.filter(o => !o.ready);
        if (notReady.length > 0) {
          // Keep isProcessing = true during polling (loader stays visible)
          clearInterval(messageInterval);
          pollVideoReadiness(notReady.map(o => o.url), videoData);
          return; // Don't hit finally block yet
        }
        
        // All videos ready immediately — dismiss loader after a short delay
        clearInterval(messageInterval);
        setTimeout(() => {
          setIsProcessing(false);
          setProcessingMessage("");
          setProcessingStep(0);
          setTimeout(() => {
            document.getElementById("video-results")?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }, 2000);
        return;
      } else {
        console.error("Response not successful:", response.data);
        toast.error("Failed to generate videos");
      }
    } catch (error) {
      console.error("Processing error:", error);
      console.error("Error details:", error.response?.data);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail 
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : "Failed to generate videos";
      toast.error(errorMsg);
      clearInterval(messageInterval);
      setIsProcessing(false);
      setProcessingMessage("");
      setProcessingStep(0);
    }
  };

  // Poll for video readiness (when backend returns URLs before Cloudinary finishes)
  const pollVideoReadiness = async (urls, videoData) => {
    const maxPolls = 40;
    let remaining = [...urls];
    console.log(`Starting poll for ${remaining.length} videos...`);
    setIsPolling(true);
    
    for (let i = 0; i < maxPolls && remaining.length > 0; i++) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        console.log(`Poll ${i + 1}: checking ${remaining.length} videos`);
        const res = await axios.post(`${API}/video/check-video-status`, { urls: remaining }, { timeout: 30000 });
        const statuses = res.data?.statuses || {};
        const detailed = res.data?.results || {};
        console.log("Poll statuses:", statuses);

        // Any URL flagged as permanently failed by Cloudinary → abort immediately
        const permanentlyFailed = Object.keys(detailed).filter(u => detailed[u]?.failed);
        if (permanentlyFailed.length > 0) {
          console.error("Cloudinary permanently rejected these URLs:", permanentlyFailed);
          const clipCount = uploadedClips.filter(c => c.include_in_stitch !== false).length;
          if (clipCount > 20) {
            toast.error(
              `Too many clips (${clipCount}) for a single render. Star your top picks and ` +
              `keep the included set to ~12-20 clips — the cloud renderer struggles past that.`
            );
          } else {
            toast.error(
              "The cloud renderer couldn't process this video. " +
              "Try a shorter mode, fewer clips, or wait a moment and try again."
            );
          }
          remaining = [];
          break;
        }

        const nowReady = Object.keys(statuses).filter(u => statuses[u]);
        
        if (nowReady.length > 0) {
          remaining = remaining.filter(u => !statuses[u]);
          
          // Update the generated videos state to mark newly ready videos
          setGeneratedVideos(prev => {
            if (!prev) return prev;
            const updated = JSON.parse(JSON.stringify(prev));
            
            if (updated.stitched_outputs) {
              updated.stitched_outputs = updated.stitched_outputs.map(o => {
                if (nowReady.some(readyUrl => o.url === readyUrl || o.secure_url === readyUrl)) {
                  console.log(`Video ${o.id} is now ready!`);
                  return { ...o, ready: true };
                }
                return o;
              });
            }
            if (updated.individual_outputs) {
              for (const k of Object.keys(updated.individual_outputs)) {
                updated.individual_outputs[k] = updated.individual_outputs[k].map(o => {
                  if (nowReady.some(readyUrl => o.url === readyUrl || o.secure_url === readyUrl)) {
                    return { ...o, ready: true };
                  }
                  return o;
                });
              }
            }
            return updated;
          });
          
          if (remaining.length === 0) {
            toast.success("All videos are ready!");
          }
        }
      } catch (err) {
        console.warn("Poll check failed:", err.message);
      }
    }
    
    if (remaining.length > 0) {
      console.warn(`Polling ended with ${remaining.length} videos still not ready`);
      toast.info("Video is taking longer than expected. Try refreshing the page in a minute.");
    }
    setIsPolling(false);
    // Dismiss the loader now that polling is done
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    setTimeout(() => {
      document.getElementById("video-results")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };


  // Copy URL
  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy URL");
    }
  };

  // Submit/Approve a stitched video
  const handleApproveVideo = async (video) => {
    setSubmittingVideo(video.id);
    try {
      const clipIds = uploadedClips
        .filter(c => c.includeInStitch)
        .map(c => c.public_id || c.id);
      
      const response = await axios.post(`${API}/video/approve`, {
        video_url: video.secure_url,
        format_key: video.format_key,
        format_label: video.format_label,
        clip_ids: clipIds,
        wms_job_id: wmsJobId || null,
        // Forward the Cloudinary assets created during generation so the
        // backend can purge them once R2 has the canonical mirror.
        cloudinary_prewarm_ids: generatedVideos?.cloudinary_prewarm_ids || [],
        cloudinary_audio_id: generatedVideos?.cloudinary_audio_id || null,
      });
      
      if (response.data.success) {
        const submission = response.data.submission;
        setSubmittedVideos(prev => ({ ...prev, [video.id]: submission }));
        toast.success("Video submitted successfully");
        
        // Poll for R2 URL in background (R2 upload happens async)
        if (submission.r2_status === 'pending' && submission.id) {
          pollForR2Url(video.id, submission.id);
        }
      }
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setSubmittingVideo(null);
    }
  };

  // Poll backend until R2 URL is populated
  const pollForR2Url = async (videoId, submissionId) => {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise(r => setTimeout(r, 10000)); // check every 10s
      try {
        const res = await axios.get(`${API}/video/submission/${submissionId}`);
        if (res.data.r2_url) {
          setSubmittedVideos(prev => ({
            ...prev,
            [videoId]: { ...prev[videoId], r2_url: res.data.r2_url, r2_status: 'uploaded' }
          }));
          toast.success("Permanent video link ready");
          return;
        }
        if (res.data.r2_status === 'failed') {
          toast.error("R2 upload failed — Cloudinary link will be used");
          return;
        }
      } catch { /* ignore poll errors */ }
    }
  };

  // Copy URL — prefer R2 link for submitted videos
  const canHubPush = user?.role === 'editor' || user?.role === 'super_admin';

  const openHubModal = async (video, assetType) => {
    setHubModal(video);
    if (assetType) setHubForm(f => ({ ...f, asset_type: assetType }));
    if (hubBranches === null || hubBranches.length === 0) {
      setHubBranches(null);
      try {
        const res = await axios.get(`${API}/social-assets/my-branches`);
        setHubBranches(res.data.branches || []);
      } catch {
        setHubBranches([]);
        toast.error("Couldn't load branches — please try again");
      }
    }
  };

  const handleHubPush = async () => {
    if (!hubForm.branch_id) return toast.error("Pick a branch");
    setHubPushing(true);
    try {
      const sub = submittedVideos[hubModal.id];
      const res = await axios.post(`${API}/social-assets/push-from-url`, {
        video_url: sub?.r2_url || hubModal.secure_url,
        branch_id: hubForm.branch_id,
        title: hubForm.title.trim(),
        asset_type: hubForm.asset_type,
        wms_job_id: wmsJobId || null,
      }, { timeout: 180000 });
      setHubPushed(prev => ({ ...prev, [hubModal.id]: true }));
      toast.success(res.data.already_pushed ? "Already in the Social Library" : "Sent to the Social Library");
      setHubModal(null);
      setHubForm(f => ({ ...f, title: "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Push failed — please try again");
    } finally {
      setHubPushing(false);
    }
  };

  const copyVideoLink = async (video) => {
    const sub = submittedVideos[video.id];
    const urlToCopy = sub?.r2_url || video.secure_url;
    try {
      await navigator.clipboard.writeText(urlToCopy);
      toast.success(sub?.r2_url ? "R2 link copied" : "Link copied (R2 uploading...)");
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get clips sorted by order
  const sortedClips = [...uploadedClips].sort((a, b) => a.order - b.order);

  // If processing, show the premium loading screen
  if (isProcessing) {
    return (
      <VideoGenerationLoader 
        hasMusic={!!selectedMusicRef.current}
        hasStitch={stitchEnabled}
        clipCount={uploadedClips.length}
      />
    );
  }

  return (
    <div className="video-builder">
      {/* Hidden audio element for music preview */}
      <audio ref={audioRef} preload="none" />

      {/* Send to Social Library modal */}
      {hubModal && (
        <div className="smp-modal-overlay" onClick={() => !hubPushing && setHubModal(null)} data-testid="hub-push-modal">
          <div className="smp-modal svb-hub-modal" onClick={(e) => e.stopPropagation()}>
            <button className="smp-modal-close" onClick={() => !hubPushing && setHubModal(null)} data-testid="hub-modal-close">
              <X size={18} />
            </button>
            <div className="svb-hub-body">
              <h3 className="svb-hub-title"><Send size={17} /> Send to Social Library</h3>
              <p className="svb-hub-sub">Push this finished video straight through to the branch's social media manager.</p>
              <div className="esu-field">
                <label>Branch</label>
                {hubBranches === null ? (
                  <div className="svb-hub-loading"><Loader2 size={14} className="spin-icon" /> Loading branches…</div>
                ) : hubBranches.length === 0 ? (
                  <button type="button" className="svb-hub-loading svb-hub-retry" onClick={() => openHubModal(hubModal)} data-testid="hub-branches-retry">
                    <RefreshCw size={14} /> No branches loaded — retry
                  </button>
                ) : (
                  <select
                    value={hubForm.branch_id}
                    onChange={(e) => setHubForm(f => ({ ...f, branch_id: e.target.value }))}
                    data-testid="hub-branch-select"
                  >
                    <option value="">Select branch…</option>
                    {hubBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                )}
              </div>
              <div className="esu-field">
                <label>Asset type</label>
                <select
                  value={hubForm.asset_type}
                  onChange={(e) => setHubForm(f => ({ ...f, asset_type: e.target.value }))}
                  data-testid="hub-type-select"
                >
                  <option value="sneak_peek">Sneak Peek</option>
                  <option value="just_listed">Just Listed</option>
                  <option value="price_reduction">Price Reduction</option>
                  <option value="recently_sold">Recently Sold</option>
                  <option value="narratour">NarraTour</option>
                  <option value="presentour">PresenTour</option>
                  <option value="post_viewing">Post Viewing</option>
                  <option value="post_valuation">Post Valuation</option>
                </select>
              </div>
              <div className="esu-field">
                <label>Property address / title</label>
                <input
                  type="text"
                  placeholder={wmsJobId ? "Leave blank to use the property address" : "e.g. 14 Castle Street, Farnham"}
                  value={hubForm.title}
                  onChange={(e) => setHubForm(f => ({ ...f, title: e.target.value }))}
                  data-testid="hub-title-input"
                />
              </div>
              <button
                className="esu-submit-btn"
                onClick={handleHubPush}
                disabled={hubPushing}
                data-testid="hub-push-submit"
              >
                {hubPushing ? <><Loader2 size={16} className="spin-icon" /> Sending…</> : <><Send size={16} /> Send to Social Library</>}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <AppHeader onBack={onBack} />

      <div className="video-content">
        <RenderHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          wmsJobId={wmsJobId ? String(wmsJobId).trim() : ""}
          onOpenOutputs={openBatchOutputs}
          onReopenEdit={reopenBatchEdit}
        />
        {batch ? (
          <CreatomateBatchResults
            batch={batch}
            onUpdate={setBatch}
            onBack={() => setBatch(null)}
            onReopenEdit={batch.has_edit_state || batch.batch_id ? () => reopenBatchEdit({ batch_id: batch.batch_id }) : null}
            submittedVideos={submittedVideos}
            submittingVideo={submittingVideo}
            onApprove={handleApproveVideo}
            onCopyLink={copyVideoLink}
            canHubPush={canHubPush}
            hubPushed={hubPushed}
            onHubPush={(video) => openHubModal(video, video.kind)}
          />
        ) : generatedVideos ? (
          <div id="video-results" className="results-section">
            <div className="results-header">
              <h2 className="results-title">Generated Videos</h2>
              <div className="results-actions">
                <span className="results-count">{generatedVideos.total_count} video{generatedVideos.total_count !== 1 ? 's' : ''}</span>
                <Button 
                  className="generate-more-btn"
                  onClick={() => { setGeneratedVideos(null); setIsPolling(false); }}
                  data-testid="generate-more-btn"
                >
                  <RefreshCw size={16} />
                  Generate More
                </Button>
              </div>
            </div>

            {/* Polling banner - shows while videos are still being processed */}
            {isPolling && (
              <div className="polling-banner" data-testid="polling-banner">
                <Loader2 size={18} className="spin-icon" />
                <span>Videos are being processed by the cloud renderer. They'll appear automatically when ready...</span>
              </div>
            )}

            {/* Stitched Outputs - Show First */}
            {generatedVideos.stitched_outputs?.length > 0 && (
              <div className="results-group featured">
                <h3 className="group-title">
                  <Layers className="group-icon" />
                  Stitched Video
                </h3>
                <div className={`videos-grid stitched ${
                  generatedVideos.stitched_outputs[0]?.format_key?.includes('landscape') ? 'landscape' 
                  : generatedVideos.stitched_outputs[0]?.format_key?.includes('square') ? 'square' : ''
                }`}>
                  {generatedVideos.stitched_outputs.map((video) => {
                    const aspectClass = video.format_key?.includes('landscape') ? 'landscape' 
                      : video.format_key?.includes('square') ? 'square'
                      : video.format_key?.includes('4_5') ? 'portrait-4-5' : '';
                    return (
                    <div key={video.id} className={`video-card stitched ${!video.ready ? 'processing' : ''}`}>
                      <div className={`video-preview ${aspectClass}`}>
                        {video.ready ? (
                          <video 
                            src={video.secure_url} 
                            controls 
                            preload="metadata"
                            poster={video.poster_url || undefined}
                          />
                        ) : (
                          <div className="video-processing-placeholder" data-testid="video-processing">
                            <Loader2 size={32} className="spin-icon" />
                            <span>Processing video...</span>
                            <span className="processing-sub">This usually takes 30-60 seconds</span>
                          </div>
                        )}
                      </div>
                      <div className="video-info">
                        <span className="video-label">{video.format_label}</span>
                        {!video.ready && <span className="processing-badge">Processing</span>}
                        {video.ready && !submittedVideos[video.id] && (
                          <span className="status-badge generated">Generated</span>
                        )}
                        {submittedVideos[video.id] && (
                          <span className="status-badge submitted">Submitted</span>
                        )}
                      </div>
                      {video.ready && (
                        <div className="video-actions">
                          {submittedVideos[video.id] ? (
                            <>
                              <button 
                                className="video-btn approve-btn submitted"
                                onClick={() => copyVideoLink(video)}
                                data-testid="copy-video-link"
                                title="Copy Video Link"
                              >
                                {submittedVideos[video.id]?.r2_url ? (
                                  <><CheckCircle size={14} /><span>Copy R2 Link</span></>
                                ) : (
                                  <><Copy size={14} /><span>Copy Link</span></>
                                )}
                              </button>
                              <a 
                                href={video.secure_url}
                                download={video.filename}
                                className="video-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download Video"
                                data-testid="download-video"
                              >
                                <Download className="video-btn-icon" />
                                <span>Download</span>
                              </a>
                              <button type="button" className="video-btn video-btn-soon" disabled title="Coming soon — pushes this video into the right place on WMS" data-testid="wms-sync-video">
                                <RefreshCw className="video-btn-icon" />
                                <span>WMS Sync</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <a 
                                href={video.secure_url}
                                download={video.filename}
                                className="video-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download Video"
                                data-testid="download-video"
                              >
                                <Download className="video-btn-icon" />
                                <span>Download</span>
                              </a>
                              <button type="button" className="video-btn video-btn-soon" disabled title="Coming soon — pushes this video into the right place on WMS" data-testid="wms-sync-video">
                                <RefreshCw className="video-btn-icon" />
                                <span>WMS Sync</span>
                              </button>
                              <button 
                                className="video-btn approve-btn"
                                onClick={() => handleApproveVideo(video)}
                                disabled={submittingVideo === video.id}
                                data-testid="approve-video"
                                title="Submit / Approve Video"
                              >
                                {submittingVideo === video.id ? (
                                  <><Loader2 size={14} className="spin-icon" /><span>Submitting...</span></>
                                ) : (
                                  <><CheckCircle size={14} /><span>Submit / Approve</span></>
                                )}
                              </button>
                            </>
                          )}
                          {canHubPush && (
                            <button
                              className={`video-btn hub-push-btn ${hubPushed[video.id] ? 'pushed' : ''}`}
                              onClick={() => !hubPushed[video.id] && openHubModal(video)}
                              disabled={!!hubPushed[video.id]}
                              data-testid="send-to-social-hub"
                              title="Send to Social Library"
                            >
                              {hubPushed[video.id] ? (
                                <><CheckCircle size={14} /><span>In Social Library</span></>
                              ) : (
                                <><Send size={14} /><span>Social Library</span></>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Individual Outputs */}
            {Object.keys(generatedVideos.individual_outputs || {}).length > 0 && (
              <div className="results-group">
                <h3 className="group-title">Individual Clips</h3>
                
                {Object.entries(generatedVideos.individual_outputs).map(([formatKey, videos]) => (
                  <div key={formatKey} className="format-group">
                    <div className="format-header">
                      <span className="format-name">{videos[0]?.format_label || formatKey}</span>
                      <span className="format-count">{videos.length} videos</span>
                    </div>
                    <div className={`videos-grid ${formatKey?.includes('landscape') ? 'landscape' : formatKey?.includes('square') ? 'square' : ''}`}>
                      {videos.map((video) => {
                        const aspectClass = formatKey?.includes('landscape') ? 'landscape' 
                          : formatKey?.includes('square') ? 'square'
                          : formatKey?.includes('4_5') ? 'portrait-4-5' : '';
                        return (
                        <div key={video.id} className={`video-card ${!video.ready ? 'processing' : ''}`}>
                          <div className={`video-preview ${aspectClass}`}>
                            {video.ready ? (
                              <video 
                                src={video.secure_url} 
                                controls 
                                preload="metadata"
                                poster={video.poster_url || undefined}
                              />
                            ) : (
                              <div className="video-processing-placeholder" data-testid="video-processing-individual">
                                <Loader2 size={24} className="spin-icon" />
                                <span>Processing...</span>
                              </div>
                            )}
                          </div>
                          {video.ready ? (
                            <div className="video-actions">
                              <a 
                                href={video.secure_url}
                                className="video-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open in new tab"
                              >
                                <ExternalLink className="video-btn-icon" />
                              </a>
                              <a 
                                href={video.secure_url}
                                download={video.filename}
                                className="video-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download"
                              >
                                <Download className="video-btn-icon" />
                              </a>
                              <button 
                                className="video-btn"
                                onClick={() => copyUrl(video.secure_url)}
                                title="Copy URL"
                              >
                                <Copy className="video-btn-icon" />
                              </button>
                            </div>
                          ) : (
                            <div className="video-info">
                              <span className="processing-badge">Processing</span>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Show form if no results yet */
          <div className="video-builder-layout">
            {/* Output tabs — click to configure, toggle to include in the render */}
            <div className="reel-type-bar" data-testid="reel-type-bar">
              {OUTPUT_KINDS.map((rt) => {
                const Icon = rt.icon;
                const locked = !rt.always && reelsLocked;
                const on = rt.always || (!locked && outputConfig[rt.value]?.enabled);
                return (
                  <button
                    key={rt.value}
                    type="button"
                    className={`reel-tab ${reelType === rt.value ? "active" : ""} ${on ? "is-on" : "is-off"} ${locked ? "is-coming-soon" : ""}`}
                    onClick={() => { if (!locked) setReelType(rt.value); }}
                    disabled={locked}
                    title={locked ? `${rt.label} reels are coming soon for editor logins — Sneak Peek only for now` : undefined}
                    data-testid={`reel-tab-${rt.value}`}
                  >
                    {rt.always ? (
                      <span className="reel-tab-toggle is-locked" title="Always included"><Lock size={12} /></span>
                    ) : locked ? (
                      <span className="reel-tab-toggle is-locked" data-testid={`reel-locked-${rt.value}`}><Lock size={12} /></span>
                    ) : (
                      <span
                        role="switch"
                        aria-checked={on}
                        className={`reel-tab-toggle ${on ? "on" : ""}`}
                        title={on ? "Included — click to skip this reel" : "Skipped — click to render this reel"}
                        onClick={(e) => { e.stopPropagation(); setOutput(rt.value, { enabled: !on }); }}
                        data-testid={`reel-toggle-${rt.value}`}
                      >
                        {on ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                      </span>
                    )}
                    <Icon size={15} />
                    <span>{rt.label}</span>
                    {locked && <span className="reel-tab-soon">Soon</span>}
                    {rt.always && uploadedClips.length > 0 && (
                      <span className="reel-tab-mode" data-testid="sneak-tab-mode" title="Sneak Peek cut style — change it on this tab">
                        {editMode === "default" ? "1.4s" : editMode === "short" ? "3–5s" : editMode === "custom" ? "Custom" : (EDIT_MODES.find((m) => m.value === editMode)?.label || editMode)}
                      </span>
                    )}
                  </button>
                );
              })}
              <span className="reel-bar-summary" data-testid="reel-bar-summary">
                <Sparkles size={13} /> {enabledOutputs.length} output{enabledOutputs.length === 1 ? "" : "s"} will render
                {parentBatch && <em className="reel-bar-version" title="Generate will render a new version linked to the reopened edit">· re-render → v{parentBatch.version + 1}</em>}
              </span>
              <button type="button" className="history-btn" onClick={() => setHistoryOpen(true)} data-testid="history-open-btn" title="Previous renders — reopen outputs or the edit">
                <History size={14} /> History
              </button>
            </div>

            {(() => {
              const kind = OUTPUT_KINDS.find((k) => k.value === reelType) || OUTPUT_KINDS[0];
              const cfg = outputConfig[kind.value] || {};
              const on = kind.always || cfg.enabled;
              const master = reelMasters.find((m) => m.kind === kind.value);
              const showPrices = kind.value === "price_reduction";
              return (
                <div className={`reel-options-card ${on ? "" : "is-off"}`} data-testid="reel-options">
                  <div className="reel-options-head reel-options-head-row">
                    <div>
                      <h4>{kind.label}{kind.always ? "" : " Reel"}</h4>
                      <p>
                        {kind.always
                          ? "Plain edit, no titles — 1.4s quick cuts by default. Pick a different cut style (or an AI mode) if you want."
                          : `Always 3–5 second cuts, trimmed to the template length · 1080×1920 · music: ${tabOwnMusic ? (outputConfig[reelType]?.music?.name || "none (own track not picked yet)") : "same as Sneak Peek"} · client logo on the final card.`}
                      </p>
                    </div>
                    {kind.always ? (
                      <span className="reel-always-chip"><Lock size={12} /> Always included</span>
                    ) : (
                      <label className="reel-on-switch" data-testid="reel-options-switch">
                        <span>{on ? "Included" : "Skipped"}</span>
                        <Switch checked={!!on} onCheckedChange={(v) => setOutput(kind.value, { enabled: v })} />
                      </label>
                    )}
                  </div>

                  {kind.always && uploadedClips.length > 0 && (() => {
                    const total = activeClips.reduce((sum, c) => sum + Math.max(0, (c.endTime ?? 0) - (c.startTime ?? 0)), 0);
                    const modeMeta = EDIT_MODES.find((m) => m.value === editMode);
                    return (
                      <div className="sneak-cut-row" data-testid="sneak-cut-row">
                        <div className="sneak-cut-chips">
                          <span className="sneak-cut-label">Cut style</span>
                          {EDIT_MODES.map((m) => (
                            <button
                              key={m.value}
                              type="button"
                              className={`sneak-cut-chip ${editMode === m.value ? "is-active" : ""}`}
                              onClick={() => setEditMode(m.value)}
                              title={m.description}
                              data-testid={`sneak-cut-${m.value}`}
                            >
                              {m.label}
                            </button>
                          ))}
                        </div>
                        <div className={`sneak-length ${total > 30 ? "is-long" : ""}`} data-testid="sneak-length">
                          <Clock size={13} />
                          <span>
                            ≈ <strong>{Math.round(total)}s</strong> from {activeClips.length} clips · {modeMeta?.label}{modeMeta?.value === "default" ? " (1.4s cuts)" : ""}
                          </span>
                          {total > 30 && editMode !== "default" && (
                            <button type="button" className="sneak-length-fix" onClick={() => setEditMode("default")} data-testid="sneak-length-fix">
                              Use 1.4s quick cuts
                            </button>
                          )}
                          {total > 30 && editMode === "default" && (
                            activeClips.length > 12 && !smartSelect.running
                              ? <button type="button" className="sneak-length-fix" onClick={() => runSmartSelect(12)} data-testid="sneak-length-smart">
                                  <Wand2 size={12} /> Smart select the best 12
                                </button>
                              : <em>Long for a sneak peek — remove a few clips</em>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="reel-fields">
                    <div className="reel-field">
                      <label>Template</label>
                      <select
                        className="reel-select"
                        value={cfg.template || (kind.always ? "plain" : "master")}
                        onChange={(e) => setOutput(kind.value, { template: e.target.value })}
                        data-testid={`reel-template-select-${kind.value}`}
                      >
                        {kind.always ? (
                          <option value="plain">Plain edit (no template)</option>
                        ) : (
                          <option value="master" disabled={master && !master.available}>
                            Reels Styler · {master?.name || kind.label}{master?.version ? ` v${master.version}` : ""}{master && !master.available ? " (no active master)" : ""}
                          </option>
                        )}
                        {creatomateTemplates.length > 0 && (
                          <optgroup label="Creatomate account templates">
                            {creatomateTemplates.map((t) => (
                              <option key={t.id} value={`template:${t.id}`}>{t.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    {!kind.always && (
                      <>
                        <div className="reel-field">
                          <label>Property name</label>
                          <input
                            value={propertyDetails.name}
                            onChange={(e) => setPropertyDetails((p) => ({ ...p, name: e.target.value }))}
                            placeholder="Fern Cottage"
                            data-testid="reel-property-name"
                          />
                        </div>
                        <div className="reel-field">
                          <label>Location</label>
                          <input
                            value={propertyDetails.location}
                            onChange={(e) => setPropertyDetails((p) => ({ ...p, location: e.target.value }))}
                            placeholder="Farley Hill"
                            data-testid="reel-property-location"
                          />
                        </div>
                        {showPrices && (
                          <>
                            <div className="reel-field">
                              <label>Old price</label>
                              <input
                                value={propertyDetails.old_price}
                                onChange={(e) => setPropertyDetails((p) => ({ ...p, old_price: e.target.value }))}
                                placeholder="£850,000"
                                data-testid="reel-old-price"
                              />
                            </div>
                            <div className="reel-field">
                              <label>New price</label>
                              <input
                                value={propertyDetails.new_price}
                                onChange={(e) => setPropertyDetails((p) => ({ ...p, new_price: e.target.value }))}
                                placeholder="£800,000"
                                data-testid="reel-new-price"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {!kind.always && (
                    <div className="reel-sequence-preview" data-testid="reel-sequence-preview">
                      <span className="reel-chip reel-chip-badge">
                        {kind.value === "just_listed" ? "NEW LISTING" : kind.value === "price_reduction" ? "PRICE REDUCTION" : "SOLD."}
                      </span>
                      <span className="reel-chip-arrow">→</span>
                      <span className="reel-chip">{(propertyDetails.name || "Fern Cottage").toUpperCase()}</span>
                      {propertyDetails.location && (
                        <><span className="reel-chip-arrow">→</span><span className="reel-chip">{propertyDetails.location.toUpperCase()}</span></>
                      )}
                      {showPrices && (
                        <>
                          <span className="reel-chip-arrow">→</span>
                          <span className="reel-chip reel-chip-strike">{propertyDetails.old_price || "£850,000"}</span>
                          <span className="reel-chip-arrow">→</span>
                          <span className="reel-chip reel-chip-accent">{propertyDetails.new_price || "£800,000"}</span>
                        </>
                      )}
                      <span className="reel-chip-arrow">→</span>
                      <span className="reel-chip reel-chip-accent">
                        {kind.value === "just_listed" ? "BOOK YOUR VIEWING" : kind.value === "price_reduction" ? "BOOK A VIEWING." : "BOOK YOUR VALUATION"}
                      </span>
                    </div>
                  )}
                  <p className="reel-shared-note">
                    {kind.always
                      ? "Reels share the property details you enter on their tabs; other wording comes from the template."
                      : "Property details are shared by every reel. All other wording keeps the template's copy."}
                  </p>
                </div>
              );
            })()}

            {/* Left Column: Settings */}
            <div className="video-settings-column">
              <div className="video-form">
                {/* Upload + Output Formats - Side by Side (PARKED — see SHOW_LOAD_PANEL) */}
                {!SHOW_LOAD_PANEL && uploadedClips.length === 0 && (
                  <div className="editor-load-hint svb-autoload-hint" data-testid="autoload-hint">
                    {isLoadingFromWms ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />}
                    <span>
                      {isLoadingFromWms
                        ? `Loading clips${wmsJobId ? ` for job #${wmsJobId}` : ""}…`
                        : "Clips load automatically — open a property from My Listings or Video Search and press “Open in editor”."}
                    </span>
                  </div>
                )}
                {SHOW_LOAD_PANEL && (
                <div className="upload-formats-row">
                  {/* Left: Upload Section */}
                  <div className="upload-column">
                    <Label className="section-title">
                      <Film className="section-icon" />
                      Load Clips
                    </Label>
                    {canLoadCaptures ? (
                      <div className="wms-job-section">
                        <div className="recent-captures-section">
                          <div className="wms-divider">
                            <span>Recent Captures</span>
                          </div>
                          {loadingCaptures ? (
                            <div className="recent-captures-loading">
                              <Loader2 size={16} className="animate-spin" />
                              <span>Loading captures...</span>
                            </div>
                          ) : recentCaptures.length > 0 ? (
                            <div className="recent-captures-list" data-testid="recent-captures-list">
                              {recentCaptures.map(prop => (
                                <button
                                  key={prop.job_id}
                                  className="recent-capture-item"
                                  onClick={() => loadCapturedProperty(prop)}
                                  disabled={isLoadingFromWms}
                                  data-testid={`capture-${prop.job_id}`}
                                >
                                  <div className="recent-capture-info">
                                    <span className="recent-capture-address">{prop.address}</span>
                                    <span className="recent-capture-meta">
                                      {prop.video_count} clip{prop.video_count !== 1 ? 's' : ''} &middot; {prop.rooms.length} room{prop.rooms.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <Film size={16} className="recent-capture-icon" />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="editor-load-hint" data-testid="no-captures-hint">
                              <Film size={16} />
                              <span>No recent captures for this branch yet.</span>
                            </div>
                          )}
                        </div>
                        <div className="wms-divider">
                          <span>or load by Job ID</span>
                        </div>
                        <div className={`wms-job-input-row ${isLoadingFromWms ? 'wms-searching' : ''}`}>
                          <div className="wms-input-wrapper">
                            <input
                              type="text"
                              placeholder="Enter Job ID (e.g., 1418940)"
                              value={wmsJobId}
                              onChange={(e) => setWmsJobId(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && loadFromWmsJob()}
                              className="wms-job-input"
                              disabled={isLoadingFromWms}
                              data-testid="wms-job-input"
                            />
                            {isLoadingFromWms && <div className="wms-search-fill" />}
                          </div>
                          <button
                            onClick={() => loadFromWmsJob()}
                            disabled={isLoadingFromWms || !wmsJobId.trim()}
                            className="wms-load-btn"
                            data-testid="wms-load-btn"
                          >
                            {isLoadingFromWms ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                <span>Searching...</span>
                              </>
                            ) : (
                              <>
                                <Download size={16} />
                                <span>Load</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="editor-load-hint" data-testid="editor-load-hint">
                        <Film size={16} />
                        <span>Clips load automatically — open a listing from your dashboard and press &ldquo;Open in Editor&rdquo;.</span>
                      </div>
                    )}
                  </div>

                  {/* Right: Output Formats 2x2 Grid */}
                  <div className="formats-column">
                    <Label className="section-title">
                      <Video className="section-icon" />
                      Output Formats
                    </Label>
                    <div className="sizes-grid-2x2">
                      {VIDEO_PRESETS.map((preset) => {
                        const isSelected = selectedFormats.includes(preset.key);
                        return (
                          <button
                            key={preset.key}
                            type="button"
                            className={`size-button-compact ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleFormat(preset.key)}
                            data-testid={`format-${preset.key}`}
                          >
                            <CheckCircle className={`check-icon ${isSelected ? 'visible' : ''}`} />
                            <Video className="size-icon" />
                            <div className="size-text">
                              <span className="size-label">{preset.label}</span>
                              <span className="size-ratio">{preset.dimensions}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}

                {/* Edit Mode Selector — Sneak Peek only. Reels are fixed 3–5s cuts and must never change this. */}
                {uploadedClips.length > 0 && reelType !== "sneak_peek" && (
                  <div className="edit-mode-section edit-mode-note" data-testid="edit-mode-reel-note">
                    <Lock size={13} />
                    <span>Reels always use 3–5s cuts. The Sneak Peek cut style ({EDIT_MODES.find((m) => m.value === editMode)?.label || "Custom"}) is set on the <button type="button" className="edit-mode-note-link" onClick={() => setReelType("sneak_peek")} data-testid="edit-mode-note-link">Sneak Peek tab</button>.</span>
                  </div>
                )}
                {uploadedClips.length > 0 && reelType === "sneak_peek" && (
                  <div className="edit-mode-section">
                    <Label className="edit-mode-label"><Film size={14} /> Sneak Peek cut style</Label>
                    <div className="edit-mode-buttons" data-testid="edit-mode-selector">
                      {EDIT_MODES.map((mode) => {
                        const Icon = mode.icon;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            className={`edit-mode-btn ${editMode === mode.value ? 'active' : ''}`}
                            onClick={() => setEditMode(mode.value)}
                            data-testid={`edit-mode-${mode.value}`}
                          >
                            <Icon size={18} />
                            <span className="edit-mode-name">{mode.label}</span>
                            <span className="edit-mode-desc">{mode.description}</span>
                          </button>
                          );
                        })}
                      </div>
                </div>
              )}

              {/* Unified Clip List with Drag & Drop */}
              {uploadedClips.length > 0 && (
                <div className="clips-section">
                  <div className="clips-header">
                    <span className="clips-count" data-testid="clips-count">
                      {activeClips.length < uploadedClips.length
                        ? <>{activeClips.length} of {uploadedClips.length} clips in the edit</>
                        : <>{uploadedClips.length} clip{uploadedClips.length !== 1 ? 's' : ''}</>}
                    </span>
                    <div className={`smart-bar ${smartSelect.running ? 'is-running' : ''} ${smartSelect.applied ? 'is-applied' : ''}`} data-testid="smart-bar">
                      <span className="smart-bar-label"><Wand2 size={13} /> Smart select</span>
                      {smartSelect.running ? (
                        <span className="smart-bar-status" data-testid="smart-bar-status">
                          <Loader2 size={13} className="spin-icon" /> Looking at {uploadedClips.length} clips…
                        </span>
                      ) : (
                        <>
                          {[12, 15, "all"].map((t) => (
                            <button
                              key={t}
                              type="button"
                              className={`smart-chip ${smartSelect.applied && smartSelect.target === t ? 'is-active' : ''}`}
                              onClick={() => runSmartSelect(t)}
                              title={t === "all" ? "Keep every clip but order them room by room" : `Pick the best ${t} clips`}
                              data-testid={`smart-target-${t}`}
                            >
                              {t === "all" ? "Order all" : `Best ${t}`}
                            </button>
                          ))}
                          {smartSelect.applied && activeClips.length < uploadedClips.length && (
                            <button type="button" className="smart-chip smart-undo" onClick={useAllClips} data-testid="smart-use-all">
                              Use all clips
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {editMode === 'drop_ai' && (
                      <span className="stitch-hint">
                        <Star size={12} className="stitch-hint-icon" />
                        Star up to 3 clips to land them on the drops
                        {uploadedClips.filter(c => c.isStarred).length > 0 && (
                          <span className="star-count"> ({uploadedClips.filter(c => c.isStarred).length}/3)</span>
                        )}
                      </span>
                    )}
                    {uploadedClips.length > 1 && (
                      <button
                        type="button"
                        className="clips-reverse-btn"
                        onClick={reverseClipOrder}
                        data-testid="reverse-clip-order-btn"
                        title="Flip the clip order (affects stitched output too)"
                      >
                        <ArrowUpDown size={14} />
                        <span>Reverse order</span>
                      </button>
                    )}
                    {floorplans.length > 0 && (
                      <button
                        type="button"
                        className="clips-reverse-btn floorplan-btn"
                        onClick={() => { setActiveFloorplanIdx(0); setFloorplanOpen(true); }}
                        data-testid="floorplan-lightbox-btn"
                        title="View the property floorplan PDF"
                      >
                        <LayoutTemplate size={14} />
                        <span>Floorplan{floorplans.length > 1 ? `s (${floorplans.length})` : ''}</span>
                      </button>
                    )}
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDndDragEnd}
                  >
                    <SortableContext
                      items={sortedClips.map(c => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="clips-list" data-testid="clips-list">
                        {sortedClips.map((clip, idx) => (
                          <SortableClipCard
                            key={clip.id}
                            clip={clip}
                            idx={idx}
                            totalCount={sortedClips.length}
                            isExpanded={expandedTrimId === clip.id}
                            onToggleTrim={(id) => setExpandedTrimId(expandedTrimId === id ? null : id)}
                            onToggleStar={toggleStar}
                            onRemove={removeClip}
                            onMoveUp={(id) => moveClip(id, 'up')}
                            onMoveDown={(id) => moveClip(id, 'down')}
                            onTrimChange={handleTrimChange}
                            formatDuration={formatDuration}
                            formatTime={formatTime}
                            showStar={editMode === 'drop_ai'}
                            onPreview={setPreviewClip}
                            isPreviewing={previewClip?.id === clip.id}
                            onToggleInclude={toggleIncludeClip}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <p className="reorder-tip">
                    Drag to reorder clips
                    {activeClips.length < uploadedClips.length && " · greyed clips are skipped — hover to preview, tap + Include to add them back"}
                  </p>
                </div>
              )}

            {/* Music Section — per output tab */}
            <div className="toggle-section" data-testid="music-section">
              <div className="toggle-row">
                <div className="toggle-label">
                  <Music className="toggle-icon" />
                  <span>{!tabIsSneak ? `Music — ${OUTPUT_KINDS.find((k) => k.value === reelType)?.label || "Reel"}` : beatPicker ? (
                    editMode === "ai_plus" ? "AI+ — Select Beat-Mapped Track" :
                    editMode === "smart_ai" ? "Smart AI — Select Beat-Mapped Track" :
                    editMode === "drop_ai" ? "Drop AI — Select Beat-Mapped Track" :
                    "AI Timing — Select Beat-Mapped Track"
                  ) : "Add Background Music"}</span>
                </div>
                {!beatPicker && tabIsSneak && (
                  <Switch
                    checked={showMusicPanel}
                    onCheckedChange={(checked) => {
                      setShowMusicPanel(checked);
                      if (checked && musicSearchResults.length === 0) {
                        searchMusic('');
                      }
                    }}
                    data-testid="music-toggle"
                  />
                )}
              </div>

              {!tabIsSneak && (
                <div className="reel-music-mode" data-testid="reel-music-mode">
                  <button
                    type="button"
                    className={`smart-chip ${!tabOwnMusic ? "is-active" : ""}`}
                    onClick={() => setOutput(reelType, { musicMode: "shared" })}
                    data-testid="reel-music-shared"
                    title="Use the Sneak Peek track on this reel"
                  >
                    Same as Sneak Peek{selectedMusic ? ` · ${selectedMusic.name}` : " · no track yet"}
                  </button>
                  <button
                    type="button"
                    className={`smart-chip ${tabOwnMusic ? "is-active" : ""}`}
                    onClick={() => {
                      setOutput(reelType, { musicMode: "own" });
                      if (musicSearchResults.length === 0) searchMusic('');
                    }}
                    data-testid="reel-music-own"
                    title="Pick a different track just for this reel"
                  >
                    Own track{tabOwnMusic && outputConfig[reelType]?.music ? ` · ${outputConfig[reelType].music.name}` : ""}
                  </button>
                  <span className="reel-music-hint">
                    {tabOwnMusic
                      ? "This reel has its own song — the Sneak Peek and other reels keep theirs."
                      : "Pick any track below and it becomes this reel's own song — the Sneak Peek and other reels are never changed from here."}
                  </span>
                </div>
              )}

              {(showMusicPanel || !tabIsSneak) && (
                <div className="music-panel" data-testid="music-panel">
                  {activeMusic && (
                    <div className={`selected-music-banner ${beatPicker ? "ai-timing-banner" : ""}`} data-testid="selected-music-banner">
                      <Music size={16} />
                      <span>
                        {activeMusic.name}
                        {!tabIsSneak && <span className="music-scope-badge">{tabOwnMusic ? "this reel only" : "from Sneak Peek"}</span>}
                        {beatPicker && activeMusic.bpm && (
                          <span className="bpm-badge">{activeMusic.bpm} BPM</span>
                        )}
                      </span>
                      <button onClick={() => setActiveMusic(null)} className="remove-music-btn" data-testid="remove-music-btn" title={tabIsSneak ? "Remove track" : "Remove — this reel renders without music (pick another below)"}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  
                  {/* AI BEAT MODES (Sneak Peek only): Show beat-mapped tracks only */}
                  {beatPicker && !activeMusic && (
                    <div className="beat-mapped-section" data-testid="beat-mapped-section">
                      <p className="beat-mapped-hint">
                        Choose a track below. Clip cuts will sync to the beat automatically.
                      </p>
                      {isLoadingBeatMapped ? (
                        <div className="beat-mapped-loading">
                          <Loader2 size={20} className="animate-spin" />
                          <span>Loading beat-mapped tracks...</span>
                        </div>
                      ) : (
                        <div className="beat-mapped-tracks" data-testid="beat-mapped-tracks">
                          {beatMappedTracks.map(track => (
                            <div
                              key={track.id}
                              className={`beat-mapped-card ${activeMusic?.id === track.id ? 'selected' : ''} ${!track.url ? 'unavailable' : ''}`}
                              data-testid={`beat-track-${track.id}`}
                            >
                              <div className="beat-card-main">
                                <div className="beat-card-info">
                                  <span className="beat-card-title">{track.title}</span>
                                  <span className="beat-card-artist">{track.artist}</span>
                                </div>
                                <div className="beat-card-meta">
                                  <span className="beat-card-bpm">{track.bpm} BPM</span>
                                  <div className="beat-card-energy">
                                    <div className="energy-bar" style={{ width: `${(track.energy || 0) * 100}%` }} />
                                  </div>
                                  <div className="beat-card-moods">
                                    {(track.mood || []).slice(0, 2).map(m => (
                                      <span key={m} className="mood-chip">{m}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="beat-card-actions">
                                {track.url && (
                                  <button
                                    className={`track-preview-btn ${previewingTrackId === track.id ? 'playing' : ''}`}
                                    onClick={() => handleMusicPreview(track)}
                                    title={previewingTrackId === track.id ? "Stop" : "Preview"}
                                    data-testid={`preview-beat-${track.id}`}
                                  >
                                    {previewingTrackId === track.id ? <Pause size={16} /> : <Play size={16} />}
                                  </button>
                                )}
                                <button
                                  className={`beat-select-btn ${activeMusic?.id === track.id ? 'selected' : ''}`}
                                  onClick={() => track.url && handleSelectMusic(track)}
                                  disabled={!track.url}
                                  title={track.url ? "Use this track" : "Track not available in R2"}
                                  data-testid={`select-beat-${track.id}`}
                                >
                                  {activeMusic?.id === track.id ? <CheckCircle size={16} /> : <Music size={16} />}
                                </button>
                              </div>
                              {trackLoadError === track.id && (
                                <div className="beat-card-error">Failed to load audio</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* NORMAL MODE: Search-based music selection */}
                  {(!tabIsSneak || editMode !== "ai_timing") && (!activeMusic || (!tabIsSneak && !tabOwnMusic)) && (
                    <>
                      <div className="music-search-row">
                        <Input
                          type="text"
                          placeholder="Search tracks..."
                          value={musicSearchQuery}
                          onChange={(e) => setMusicSearchQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchMusic(musicSearchQuery)}
                          className="music-search-input"
                          data-testid="music-search-input"
                        />
                        <Button
                          onClick={() => searchMusic(musicSearchQuery)}
                          disabled={isSearchingMusic}
                          size="sm"
                          data-testid="music-search-btn"
                        >
                          {isSearchingMusic ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                        </Button>
                      </div>

                      <div className="music-suggestions">
                        <span className="suggestions-label">Vibes:</span>
                        {(musicVibes.length > 0 ? musicVibes : ['upbeat', 'chill', 'cinematic', 'luxury', 'emotional', 'modern', 'general']).map(v => (
                          <button
                            key={v}
                            className="suggestion-chip"
                            onClick={() => {
                              setMusicSearchQuery('');
                              searchMusic('', v);
                            }}
                            data-testid={`vibe-chip-${v}`}
                          >
                            {v}
                          </button>
                        ))}
                        <button
                          className="suggestion-chip suggestion-chip-all"
                          onClick={() => { setMusicSearchQuery(''); searchMusic(''); }}
                          data-testid="vibe-chip-all"
                        >
                          all
                        </button>
                      </div>

                      {musicSearchResults.length > 0 && (
                        <div className="music-results">
                          {musicSearchResults.map(track => (
                            <MusicTrackCard
                              key={track.id}
                              track={track}
                              isSelected={activeMusic?.id === track.id}
                              onSelect={handleSelectMusic}
                              onPreview={handleMusicPreview}
                              isPlaying={previewingTrackId === track.id}
                              loadError={trackLoadError === track.id}
                            />
                          ))}
                        </div>
                      )}

                      <p className="music-attribution">
                        149 tracks from your R2 music library
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Logo Watermark Section - Matching Toggle Style */}
            <div className="logo-section">
              <div className="section-header-row">
                <div className="section-header-left">
                  <ImageIcon size={16} className="section-icon" />
                  <span>Logo Watermark</span>
                </div>
                <div className="section-header-right">
                  <Switch
                    checked={logoEnabled}
                    onCheckedChange={setLogoEnabled}
                    data-testid="logo-enabled-toggle"
                  />
                  {logoEnabled && (
                    <button 
                      className={`settings-cog-btn ${showLogoSettings ? 'active' : ''}`}
                      onClick={() => setShowLogoSettings(!showLogoSettings)}
                      data-testid="logo-settings-btn"
                    >
                      <Settings size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              {showLogoSettings && logoEnabled && (
                <div className="logo-settings-compact">
                  {wmsClient?.id && (
                    <div className="logo-client-note" data-testid="logo-client-note">
                      Logo &amp; placement remembered for <strong>{wmsClient.name || "this client"}</strong>
                    </div>
                  )}
                  {/* All controls in one clean row */}
                  <div className="logo-controls-row">
                    {/* Logo Upload/Preview */}
                    <div className="logo-upload-cell">
                      {companyLogo && !companyLogo.broken ? (
                        <div className="logo-thumb" data-testid="logo-thumb">
                          <img
                            src={companyLogo.secure_url || companyLogo.url}
                            alt="Logo"
                            onError={() => { setCompanyLogo((l) => (l ? { ...l, broken: true } : l)); toast.error("Saved logo file is missing — please upload it again"); }}
                          />
                          <button className="logo-remove-x" onClick={() => setCompanyLogo(null)} title="Remove logo">
                            <X size={10} />
                          </button>
                          <button className="logo-replace-btn" onClick={() => logoInputRef.current?.click()} disabled={isUploadingLogo} title="Replace logo" data-testid="logo-replace-btn">
                            {isUploadingLogo ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                          </button>
                        </div>
                      ) : companyLogo?.broken ? (
                        <button
                          className="logo-upload-small is-broken"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={isUploadingLogo}
                          title="Saved logo file is missing — upload again"
                          data-testid="logo-upload-broken"
                        >
                          {isUploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <><AlertTriangle size={13} /> <span>Upload</span></>}
                        </button>
                      ) : (
                        <button 
                          className="logo-upload-small"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={isUploadingLogo}
                          title="Upload logo"
                          data-testid="logo-upload-btn"
                        >
                          {isUploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        </button>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                    </div>

                    {/* Position */}
                    <div className="logo-control-item">
                      <label>Position</label>
                      <select value={logoPosition} onChange={(e) => setLogoPosition(e.target.value)}>
                        {LOGO_POSITIONS.map(pos => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Size */}
                    <div className="logo-control-item">
                      <label>Size</label>
                      <div className="slider-compact">
                        <input type="range" min="5" max="90" value={logoScale} onChange={(e) => setLogoScale(Number(e.target.value))} />
                        <span>{logoScale}%</span>
                      </div>
                    </div>

                    {/* Padding */}
                    <div className="logo-control-item">
                      <label>Padding</label>
                      <div className="slider-compact">
                        <input type="range" min="0" max="60" value={logoPadding} onChange={(e) => setLogoPadding(Number(e.target.value))} />
                        <span>{logoPadding}px</span>
                      </div>
                    </div>

                    {/* Opacity */}
                    <div className="logo-control-item">
                      <label>Opacity</label>
                      <div className="slider-compact">
                        <input type="range" min="20" max="100" value={logoOpacity} onChange={(e) => setLogoOpacity(Number(e.target.value))} />
                        <span>{logoOpacity}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={batchSubmitting || uploadedClips.length === 0}
              className="generate-button"
              data-testid="generate-btn"
            >
              {batchSubmitting ? <Loader2 className="btn-icon spin-icon" /> : <Play className="btn-icon" />}
              {batchSubmitting
                ? "Starting renders…"
                : `Generate Social Video Outputs${enabledOutputs.length > 1 ? ` (${enabledOutputs.length})` : ""}`}
            </Button>
              </div>
            </div>

            {/* Right Column: Sticky Preview */}
            <div className="video-preview-column">
              <div className="video-sticky-preview">
                <div className="preview-header-label">
                  <span>Live Preview</span>
                </div>
                <div className={`sticky-preview-frame aspect-${(selectedFormats.includes('portrait_9_16') ? '9:16' : 
                              selectedFormats.includes('square_1_1') ? '1:1' : 
                              selectedFormats.includes('landscape_16_9') ? '16:9' : '4:5').replace(':', '-')}`}>
                  {previewClip ? (
                    <video
                      key={previewClip.id}
                      src={previewClip.r2Url || previewClip.url}
                      className="preview-bg-video"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      onLoadedMetadata={(e) => e.currentTarget.play().catch(() => {})}
                      data-testid="live-preview-video"
                    />
                  ) : uploadedClips.length > 0 ? (
                    uploadedClips[0].thumbnail_url ? (
                      <img 
                        src={uploadedClips[0].thumbnail_url} 
                        alt="Preview" 
                        className="preview-bg-img" 
                      />
                    ) : (
                      <video
                        src={`${uploadedClips[0].r2Url || uploadedClips[0].url}#t=0.5`}
                        preload="metadata"
                        muted
                        playsInline
                        className="preview-bg-video"
                      />
                    )
                  ) : (
                    <div className="preview-placeholder-text">
                      <Video size={32} />
                      <span>Load clips to see a preview</span>
                    </div>
                  )}
                  {previewClip && (
                    <div className="preview-clip-pill" data-testid="preview-clip-pill">
                      <span className="preview-clip-name">{previewClip.original_filename || previewClip.name || 'Clip'}</span>
                      <button
                        type="button"
                        onClick={() => setPreviewClip(null)}
                        className="preview-clip-close"
                        data-testid="preview-clip-close"
                        title="Close preview"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                  
                  {/* Render text overlays - grouped approach */}
                  {(() => {
                    // Group overlays by position for stacking
                    const activeOverlays = Object.entries(propertyOverlays)
                      .filter(([_, config]) => config.enabled && config.value);
                    
                    // Sort by order if available
                    activeOverlays.sort((a, b) => (a[1].order || 0) - (b[1].order || 0));
                    
                    // Get position style helper function
                    const getPositionStyle = (pos) => {
                      let style = { position: 'absolute' };
                      
                      // Horizontal positioning
                      if (pos.includes('west')) {
                        style.left = '5%';
                        style.textAlign = 'left';
                      } else if (pos.includes('east')) {
                        style.right = '5%';
                        style.textAlign = 'right';
                      } else {
                        // Center horizontally (north, south, center, mid_north, mid_south)
                        style.left = '50%';
                        style.transform = 'translateX(-50%)';
                        style.textAlign = 'center';
                      }
                      
                      // Vertical positioning
                      if (pos === 'north' || pos === 'north_west' || pos === 'north_east') {
                        style.top = '5%';
                      } else if (pos === 'south' || pos === 'south_west' || pos === 'south_east') {
                        style.bottom = '5%';
                      } else if (pos === 'mid_north' || pos === 'mid_north_west' || pos === 'mid_north_east') {
                        style.top = '25%';
                      } else if (pos === 'mid_south' || pos === 'mid_south_west' || pos === 'mid_south_east') {
                        style.bottom = '25%';
                      } else {
                        // center, west, east - vertical center
                        style.top = '50%';
                        style.transform = (style.transform || '') + ' translateY(-50%)';
                      }
                      
                      return style;
                    };
                    
                    const getFontSize = (size) => {
                      const sizes = { small: 12, medium: 16, large: 20, xlarge: 26 };
                      return sizes[size] || 16;
                    };
                    
                    // Always render grouped (groupSamePosition is always true now)
                    if (activeOverlays.length > 0) {
                      const textAlign = sharedPosition.includes('east') ? 'right' : sharedPosition.includes('west') ? 'left' : 'center';
                      
                      return (
                        <div
                          key="grouped-overlays"
                          className="preview-text-group"
                          style={{
                            ...getPositionStyle(sharedPosition),
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            backgroundColor: groupBackground.enabled ? groupBackground.color : 'transparent',
                            borderRadius: groupBackground.enabled && groupBackground.rounded ? '8px' : '0',
                            padding: groupBackground.enabled ? '0.5rem 0.75rem' : '0',
                            textAlign: textAlign,
                            zIndex: 10,
                          }}
                        >
                          {activeOverlays.map(([key, config]) => {
                            const showShadow = config.textShadow !== false;
                            return (
                              <div
                                key={key}
                                style={{
                                  fontSize: `${getFontSize(config.fontSize)}px`,
                                  fontFamily: config.font || 'Arial',
                                  color: config.color,
                                  textShadow: showShadow 
                                    ? (config.color === '#FFFFFF' || config.color === '#f5f5f5' 
                                      ? '1px 1px 3px rgba(0,0,0,0.8)' 
                                      : '1px 1px 3px rgba(255,255,255,0.5)')
                                    : 'none',
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                }}
                              >
                                {config.value}
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                  
                  {/* Render logo preview */}
                  {logoEnabled && companyLogo && (() => {
                    // Default padding percentage from edge
                    const defaultPadding = 4; // 4% default spacing from edge
                    const paddingPercent = `${Math.max(defaultPadding, logoPadding / 2)}%`;
                    
                    // Check if position is on the right side
                    const isRightSide = logoPosition.includes('east');
                    
                    // Position map for all 15 positions
                    const positionMap = {
                      // Top row
                      north_west: { top: paddingPercent, left: paddingPercent },
                      north: { top: paddingPercent, left: '50%', transform: 'translateX(-50%)' },
                      north_east: { top: paddingPercent, right: paddingPercent },
                      // Mid-top row
                      mid_north_west: { top: '25%', left: paddingPercent },
                      mid_north: { top: '25%', left: '50%', transform: 'translateX(-50%)' },
                      mid_north_east: { top: '25%', right: paddingPercent },
                      // Middle row
                      west: { top: '50%', left: paddingPercent, transform: 'translateY(-50%)' },
                      center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
                      east: { top: '50%', right: paddingPercent, transform: 'translateY(-50%)' },
                      // Mid-bottom row
                      mid_south_west: { bottom: '25%', left: paddingPercent },
                      mid_south: { bottom: '25%', left: '50%', transform: 'translateX(-50%)' },
                      mid_south_east: { bottom: '25%', right: paddingPercent },
                      // Bottom row
                      south_west: { bottom: paddingPercent, left: paddingPercent },
                      south: { bottom: paddingPercent, left: '50%', transform: 'translateX(-50%)' },
                      south_east: { bottom: paddingPercent, right: paddingPercent },
                    };
                    
                    const posStyle = positionMap[logoPosition] || positionMap.south_east;
                    
                    return (
                      <div 
                        className="preview-logo-element" 
                        style={{
                          position: 'absolute',
                          opacity: logoOpacity / 100,
                          width: '100%',
                          height: '100%',
                          top: 0,
                          left: 0,
                          pointerEvents: 'none',
                        }}
                      >
                        <img 
                          src={companyLogo.secure_url || companyLogo.url} 
                          alt="Logo" 
                          onError={(e) => { e.target.style.display = 'none'; }}
                          style={{ 
                            position: 'absolute',
                            width: `${logoScale}%`,
                            maxWidth: '90%',
                            height: 'auto',
                            ...posStyle
                          }}
                        />
                      </div>
                    );
                  })()}
                </div>
                
                {/* Format info */}
                <div className="preview-format-info">
                  {selectedFormats.map(format => {
                    const formatInfo = VIDEO_PRESETS.find(f => f.key === format);
                    return formatInfo ? (
                      <span key={format} className="format-tag">{formatInfo.label}</span>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Floorplan PDF Lightbox */}
        {floorplanOpen && floorplans.length > 0 && (
          <div className="floorplan-lightbox-overlay" onClick={() => setFloorplanOpen(false)} data-testid="floorplan-lightbox">
            <div className="floorplan-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <div className="floorplan-lightbox-header">
                <div className="floorplan-lightbox-title">
                  <LayoutTemplate size={16} />
                  <span>Floorplan — Job {floorplanJobId}</span>
                </div>
                {floorplans.length > 1 && (
                  <div className="floorplan-lightbox-tabs">
                    {floorplans.map((fp, idx) => (
                      <button
                        key={fp.id}
                        type="button"
                        className={`floorplan-tab ${idx === activeFloorplanIdx ? 'active' : ''}`}
                        onClick={() => setActiveFloorplanIdx(idx)}
                        data-testid={`floorplan-tab-${idx}`}
                      >
                        {fp.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="floorplan-lightbox-actions">
                  <a
                    href={floorplans[activeFloorplanIdx]?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="floorplan-download-btn"
                    title="Download PDF"
                    data-testid="floorplan-download-link"
                  >
                    <Download size={15} />
                  </a>
                  <button
                    type="button"
                    className="floorplan-close-btn"
                    onClick={() => setFloorplanOpen(false)}
                    data-testid="floorplan-close-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              <div className="floorplan-lightbox-body">
                <img
                  key={floorplans[activeFloorplanIdx]?.id}
                  src={`${API}/listings/${floorplanJobId}/floorplans/${floorplans[activeFloorplanIdx]?.id}/image`}
                  alt="Floorplan"
                  className="floorplan-lightbox-img"
                  data-testid="floorplan-image"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SocialVideoBuilder;
